import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  Play,
  Target,
  Users,
  Award,
  FileText,
  Download,
  Home,
  Trophy,
  MessageSquare,
  Lock,
  AlertTriangle
} from 'lucide-react';
import { Footer } from './Footer';
import { apiUrl } from '../config/api';
import { WaitlistForm } from './WaitlistForm';
import { PDFTeaser } from './PDFTeaser';
import { UpsellBanner } from './UpsellBanner';
import { trackPDFDownload } from '../utils/analytics';

interface User {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  status: string;
  userType?: string;
}

interface ModuleContentTemplateProps {
  user: User | null;
  onLogout: () => void;
  moduleId: number;
  moduleConfig: ModuleConfig;
  isPreviewMode?: boolean;
  invitationEmail?: string;
  isAdminMode?: boolean;
  inviteToken?: string;
}

interface PDFTeaserConfig {
  title: string;
  subtitle: string;
  description: string;
  urgency?: {
    text: string;
    bonus?: string;
  };
  teasers: {
    title: string;
    items: string[];
  };
  socialProof?: {
    testimonial?: string;
    author?: string;
    company?: string;
    roiData?: string;
  };
  ctaText: string;
}

interface UpsellConfig {
  type: 'advanced' | 'masterclass' | 'consulting';
  title: string;
  description: string;
  ctaText: string;
  ctaLink?: string;
  features?: string[];
}

interface ModuleConfig {
  title: string;
  description: string;
  duration: string;
  sections: ModuleSection[];
  resources: ModuleResource[];
  quizQuestions: QuizQuestion[];
  pdfTeaser?: PDFTeaserConfig;
  upsells?: UpsellConfig[];
}

interface ModuleSection {
  id: number;
  title: string;
  shortTitle?: string;
  icon: React.ComponentType<any>;
  content: React.ReactNode;
}

interface ModuleResource {
  title: string;
  type: 'pdf' | 'template' | 'checklist' | 'link';
  url: string;
  downloadable: boolean;
}

interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correct: number;
  explanation: string;
}

type CourseFeedbackResult = 'submitted' | 'dismissed';

interface CourseFeedbackEventDetail {
  source?: 'module' | 'navbar';
  moduleId?: number;
  userEmail?: string;
  onComplete?: (result: CourseFeedbackResult) => void;
}

export function ModuleContentTemplate({ 
  user, 
  onLogout, 
  moduleId, 
  moduleConfig,
  isPreviewMode = false, 
  invitationEmail: propInvitationEmail, 
  isAdminMode = false, 
  inviteToken 
}: ModuleContentTemplateProps) {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<{ [key: number]: string }>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isModuleCompleted, setIsModuleCompleted] = useState(false);
  const [roleDefinitionForm, setRoleDefinitionForm] = useState({
    role: '',
    skills: '',
    motivators: '',
    companyName: '',
    companyDescription: '',
    companyLink: '',
    jdLink: '',
    jdText: ''
  });
  const [isSubmittingRoleDefinition, setIsSubmittingRoleDefinition] = useState(false);
  const [showRoleDefinitionSuccessModal, setShowRoleDefinitionSuccessModal] = useState(false);
  const [hasJDInSubmission, setHasJDInSubmission] = useState(false);
  const [employerBrandAuditForm, setEmployerBrandAuditForm] = useState({
    careerPageUrl: '',
    improvements: ''
  });
  const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);
  const [showEmployerBrandAuditSuccessModal, setShowEmployerBrandAuditSuccessModal] = useState(false);
  const [screeningEngagementForm, setScreeningEngagementForm] = useState({
    role: '',
    email: '',
    question1: '',
    question2: '',
    question3: ''
  });
  const [isSubmittingScreening, setIsSubmittingScreening] = useState(false);
  const [showScreeningEngagementSuccessModal, setShowScreeningEngagementSuccessModal] = useState(false);
  const [pipelinePlanForm, setPipelinePlanForm] = useState({
    role: '',
    sourcingChannels: '',
    nurturingTactics: ''
  });
  const [isSubmittingPipeline, setIsSubmittingPipeline] = useState(false);
  const [showPipelinePlanSuccessModal, setShowPipelinePlanSuccessModal] = useState(false);
  // Module 3 Sourcing Practice form state
  const [sourcingPracticeForm, setSourcingPracticeForm] = useState({
    role: '',
    skills: '',
    motivators: '',
    sourcingChannels: '',
    booleanSearch: '',
    githubMessage: '',
    community: ''
  });
  const [isSubmittingSourcingPractice, setIsSubmittingSourcingPractice] = useState(false);
  const [showSourcingPracticeSuccessModal, setShowSourcingPracticeSuccessModal] = useState(false);
  const [datasetAnalysisForm, setDatasetAnalysisForm] = useState({
    role: '',
    improvements: ''
  });
  const [isSubmittingDataset, setIsSubmittingDataset] = useState(false);
  const [showDatasetAnalysisSuccessModal, setShowDatasetAnalysisSuccessModal] = useState(false);
  const [actionPlanForm, setActionPlanForm] = useState({
    role: '',
    action1: '',
    owner1: '',
    deadline1: '',
    outcome1: '',
    action2: '',
    owner2: '',
    deadline2: '',
    outcome2: '',
    action3: '',
    owner3: '',
    deadline3: '',
    outcome3: ''
  });
  const [isSubmittingActionPlan, setIsSubmittingActionPlan] = useState(false);
  const [showActionPlanSuccessModal, setShowActionPlanSuccessModal] = useState(false);
  // Module 8 Hands-On Activity form state (section 5)
  const [handsOnActivityForm, setHandsOnActivityForm] = useState({
    // Challenge Identification Form (30 min Prep)
    role: '',
    challenge1: '',
    challenge2: '',
    challenge3: '',
    metrics: '',
    targets: '',
    // Action Plan Framework (45 min Framework)
    action1: '',
    owner1: '',
    deadline1: '',
    outcome1: '',
    action2: '',
    owner2: '',
    deadline2: '',
    outcome2: '',
    action3: '',
    owner3: '',
    deadline3: '',
    outcome3: ''
  });
  const [isSubmittingHandsOnActivity, setIsSubmittingHandsOnActivity] = useState(false);
  const [showHandsOnActivitySuccessModal, setShowHandsOnActivitySuccessModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showCourseCompletionModal, setShowCourseCompletionModal] = useState(false);
  // Module 1 Interactive Reflection Activity state
  const [userChallenges, setUserChallenges] = useState(['', '', '']);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmittingChallenges, setIsSubmittingChallenges] = useState(false);

  const moduleTitle = moduleConfig?.title || `Module ${moduleId}`;

  // Function to handle section changes with smooth scrolling
  const handleSectionChange = (newSection: number) => {
    setCurrentSection(newSection);
    // Scroll to top of content area smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Module completion status is now fetched from backend via Dashboard
  // No localStorage check needed - progress is tracked in database per userId

  // Listen for the custom event to show interest form
  useEffect(() => {
    const handleShowInterestForm = (event: Event) => {
      const customEvent = event as CustomEvent<{
        formType?: 'request-access' | 'team-invitation';
        invitationEmail?: string;
        inviteToken?: string | null;
      }>;
      const detail = customEvent.detail;
      const homeUrl = inviteToken ? `/?invite=${inviteToken}` : '/';
      navigate(homeUrl);
      setTimeout(() => {
        const forwardedEvent = new CustomEvent('showInterestForm', { detail });
        window.dispatchEvent(forwardedEvent);
      }, 100);
    };

    window.addEventListener('showInterestForm', handleShowInterestForm);
    
    return () => {
      window.removeEventListener('showInterestForm', handleShowInterestForm);
    };
  }, [navigate, inviteToken]);

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadSingleFile = async (filename: string) => {
    // Try API route first (for authenticated downloads)
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const response = await fetch(apiUrl(`/api/files/download/${filename}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return true;
        }
      } catch (apiError) {
        console.warn('API download failed, trying direct link:', apiError);
      }
    }

    // Fallback to direct public URL
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };

  const handleResourceDownload = async (filename: string) => {
    if (isDownloading) return; // Prevent multiple clicks
    
    setIsDownloading(true);
    
    try {
      // Track download event
      trackPDFDownload({
        moduleId,
        filename,
        userId: user?.id,
        userTier: user?.userType || 'free'
      });

      await downloadSingleFile(filename);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleMultipleResourceDownload = async (filenames: string[]) => {
    if (isDownloading) return; // Prevent multiple clicks
    
    setIsDownloading(true);
    
    try {
      // Track download events for each file
      filenames.forEach(filename => {
        trackPDFDownload({
          moduleId,
          filename,
          userId: user?.id,
          userTier: user?.userType || 'free'
        });
      });

      // Download files sequentially with small delays to avoid browser blocking
      for (let i = 0; i < filenames.length; i++) {
        await downloadSingleFile(filenames[i]);
        if (i < filenames.length - 1) {
          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (error) {
      console.error('Error downloading files:', error);
      alert('Error downloading files. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const normalizeUrl = (url: string) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    const prefixed = /^(https?:)?\/\//i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
    return prefixed;
  };

  const handleRoleDefinitionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that at least one field is filled
    const hasAtLeastOneField = 
      roleDefinitionForm.role.trim() !== '' ||
      roleDefinitionForm.skills.trim() !== '' ||
      roleDefinitionForm.motivators.trim() !== '' ||
      roleDefinitionForm.companyName.trim() !== '' ||
      roleDefinitionForm.companyDescription.trim() !== '' ||
      roleDefinitionForm.companyLink.trim() !== '' ||
      roleDefinitionForm.jdLink.trim() !== '' ||
      roleDefinitionForm.jdText.trim() !== '';
    
    if (!hasAtLeastOneField) {
      alert('Please fill in at least one field so we can provide personalized feedback.');
      return;
    }
    
    setIsSubmittingRoleDefinition(true);

    try {
      const submission = {
        role: roleDefinitionForm.role,
        skills: roleDefinitionForm.skills,
        motivators: roleDefinitionForm.motivators,
        companyName: roleDefinitionForm.companyName,
        companyDescription: roleDefinitionForm.companyDescription,
        companyLink: normalizeUrl(roleDefinitionForm.companyLink),
        jdLink: normalizeUrl(roleDefinitionForm.jdLink),
        jdText: roleDefinitionForm.jdText,
        submittedAt: new Date().toISOString(),
        moduleId: moduleId,
        sectionId: 4
      };

      const token = localStorage.getItem('authToken');
      const response = await fetch(apiUrl('/api/module-submissions/role-definition-submission'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(submission)
      });

      const data = await response.json();

      if (data.success) {
        // Check if JD (link or text) was provided
        const hasJD = roleDefinitionForm.jdLink.trim() !== '' || roleDefinitionForm.jdText.trim() !== '';
        setHasJDInSubmission(hasJD);
        setShowRoleDefinitionSuccessModal(true);
        setRoleDefinitionForm({
          role: '',
          skills: '',
          motivators: '',
          companyName: '',
          companyDescription: '',
          companyLink: '',
          jdLink: '',
          jdText: ''
        });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit role definition. Please try again.');
    } finally {
      setIsSubmittingRoleDefinition(false);
    }
  };

  const handleSourcingPracticeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingSourcingPractice(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(apiUrl('/api/module-submissions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          moduleId: '3',
          challenges: [
            `Role: ${sourcingPracticeForm.role}`,
            `Skills: ${sourcingPracticeForm.skills}`,
            `Motivators: ${sourcingPracticeForm.motivators}`,
            `Sourcing Channels: ${sourcingPracticeForm.sourcingChannels}`,
            `Boolean Search: ${sourcingPracticeForm.booleanSearch}`,
            `GitHub Message: ${sourcingPracticeForm.githubMessage}`,
            `Community: ${sourcingPracticeForm.community}`
          ],
          userEmail: user?.email || propInvitationEmail || undefined,
          userToken: inviteToken || undefined,
          userName: user?.fullName || undefined,
          company: user?.company || undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowSourcingPracticeSuccessModal(true);
        setSourcingPracticeForm({
          role: '',
          skills: '',
          motivators: '',
          sourcingChannels: '',
          booleanSearch: '',
          githubMessage: '',
          community: ''
        });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit sourcing practice. Please try again.');
    } finally {
      setIsSubmittingSourcingPractice(false);
    }
  };

  // Check if module is already completed when component loads
  useEffect(() => {
    const checkModuleCompletion = async () => {
      // Only check for authenticated users, not preview mode
      if (!user || isPreviewMode || isAdminMode) {
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const response = await fetch(apiUrl('/api/user-progress'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.success && data.data.completedModuleIds) {
          const isCompleted = data.data.completedModuleIds.includes(moduleId);
          if (isCompleted) {
            setIsModuleCompleted(true);
            console.log(`✅ Module ${moduleId} is already completed`);
          }
        }
      } catch (error) {
        console.error('Error checking module completion:', error);
        // Don't show error to user, just log it
      }
    };

    checkModuleCompletion();
  }, [user, isPreviewMode, isAdminMode, moduleId]);

  const handleCompleteModule = async () => {
    // Prevent multiple clicks if already completing or completed
    if (isCompleting || isModuleCompleted) {
      return;
    }

    setIsCompleting(true);
    
    try {
      const completionData = {
        moduleId: moduleId.toString(),
        completedAt: new Date().toISOString(),
        userEmail: user?.email || undefined,
        userName: user?.fullName || undefined,
        company: user?.company || undefined,
        isAdminMode: isAdminMode, // Flag to mark admin testing completions
        completionDetails: {
          sectionsViewed: moduleConfig.sections.length
        }
      };

      const token = localStorage.getItem('authToken');
      const response = await fetch(apiUrl('/api/module-submissions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(completionData)
      });

      const result = await response.json();

      if (result.success) {
        // Module completion saved to database with userId
        // Set state immediately
        setIsModuleCompleted(true);
        
          // If admin mode, show special message
          if (isAdminMode) {
            console.log(`✅ Admin test completion recorded for Module ${moduleId} (does not affect user progress)`);
            // Admin doesn't see feedback modal
            setShowCompletionModal(true);
          } else {
            console.log(`✅ Module ${moduleId} completion saved for user`);
            
            // For modules 3, 6, and 8: Show feedback modal FIRST, then completion modal
            if (moduleId === 3 || moduleId === 6 || moduleId === 8) {
              if (!isAdminMode && user?.email) {
                // Check module-specific feedback status (not global)
                const moduleFeedbackKey = `module${moduleId}_feedback_submitted_${user.email}`;
                const moduleFeedbackSubmitted = localStorage.getItem(moduleFeedbackKey) === 'true';
                
                // Also check legacy global status for backward compatibility
                const globalFeedbackKey = `course_feedback_status_${user.email}`;
                const globalFeedbackSubmitted = localStorage.getItem(globalFeedbackKey) === 'submitted';
                
                const shouldRequestFeedback = !moduleFeedbackSubmitted && !globalFeedbackSubmitted;

                if (shouldRequestFeedback) {
                  console.log(`✅ Module ${moduleId} completed - Showing feedback modal FIRST before completion modal`);
                  
                  // IMPORTANT: Don't show completion modal yet - wait for feedback to complete
                  // The completion modal will be shown in the handleFeedbackComplete callback
                  const handleFeedbackComplete = (result: CourseFeedbackResult) => {
                    console.log(`✅ Feedback completed for module ${moduleId}, result:`, result);
                    
                    // Mark feedback as submitted only if user actually submitted it
                    if (result === 'submitted' && user?.email) {
                      // Mark this specific module's feedback as submitted
                      localStorage.setItem(`module${moduleId}_feedback_submitted_${user.email}`, 'true');
                      console.log(`✅ Marked module ${moduleId} feedback as submitted`);
                    }
                    // Note: If dismissed, we don't mark as submitted, so it will show again next time

                    // ALWAYS show the completion modal after feedback modal closes (whether submitted or dismissed)
                    console.log(`✅ Showing completion modal for module ${moduleId} after feedback`);
                    if (moduleId === 8) {
                      setShowCourseCompletionModal(true);
                    } else {
                      setShowCompletionModal(true);
                    }
                  };

                  const eventDetail: CourseFeedbackEventDetail = {
                    source: 'module',
                    moduleId,
                    userEmail: user.email, 
                    onComplete: (result) => {
                      handleFeedbackComplete(result);
                    }
                  };

                  // Trigger feedback modal - completion modal will show after feedback is submitted/skipped
                  window.dispatchEvent(
                    new CustomEvent<CourseFeedbackEventDetail>('showCourseFeedback', {
                      detail: eventDetail
                    })
                  );
                  
                  // CRITICAL: Don't show completion modal here - wait for feedback callback
                  return; // Exit early, completion modal will be shown in callback
                } else {
                  // Feedback already submitted for this module, show completion modal directly
                  console.log(`✅ Module ${moduleId} feedback already submitted, showing completion modal directly`);
                  if (moduleId === 8) {
                    setShowCourseCompletionModal(true);
                  } else {
                    setShowCompletionModal(true);
                  }
                }
              } else {
                // Admin mode or no user email - skip feedback, show completion modal
                if (moduleId === 8) {
                  setShowCourseCompletionModal(true);
                } else {
                  setShowCompletionModal(true);
                }
              }
            } else {
              // Other modules (1, 2, 4, 5, 7) - show completion modal directly
              setShowCompletionModal(true);
            }
          }
        
        // Refresh progress from API to ensure consistency
        if (user && !isPreviewMode && !isAdminMode) {
          try {
            const token = localStorage.getItem('authToken');
            if (token) {
              const progressResponse = await fetch(apiUrl('/api/user-progress'), {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                }
              });
              const progressData = await progressResponse.json();
              if (progressData.success && progressData.data.completedModuleIds) {
                const isCompleted = progressData.data.completedModuleIds.includes(moduleId);
                setIsModuleCompleted(isCompleted);
                console.log(`✅ Progress refreshed, Module ${moduleId} completed:`, isCompleted);
              }
            }
          } catch (progressError) {
            console.error('Error refreshing progress:', progressError);
            // Don't fail the completion if progress refresh fails
          }
        }
        
        // Reset completing state after successful completion
        setIsCompleting(false);
      } else {
        console.error('❌ Error completing module:', result.message);
        // If there's an error, don't set as completed
        setIsCompleting(false);
      }
    } catch (error) {
      console.error('Error completing module:', error);
      // If there's an error, don't set as completed
      setIsCompleting(false);
    }
  };

  const handleQuizAnswer = (questionId: number, answer: string) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleQuizSubmit = () => {
    setShowQuizResults(true);
  };

  const getScore = () => {
    const correct = moduleConfig.quizQuestions.filter((q) => {
      return quizAnswers[q.id] === q.options[q.correct];
    }).length;
    return Math.round((correct / moduleConfig.quizQuestions.length) * 100);
  };

  // Module 1 Interactive Reflection Activity handlers
  const handleChallengeChange = (index: number, value: string) => {
    const newChallenges = [...userChallenges];
    newChallenges[index] = value;
    setUserChallenges(newChallenges);
  };

  const handleSubmitChallenges = async () => {
    // Validate that at least one challenge is filled
    const filledChallenges = userChallenges.filter(challenge => challenge.trim() !== '');
    if (filledChallenges.length === 0) {
      setSubmitMessage('Please fill in at least one challenge before submitting.');
      return;
    }

    setIsSubmittingChallenges(true);
    setSubmitMessage('');

    try {
      const submissionData = {
        moduleId: '1',
        challenges: filledChallenges,
        quizAnswers: Object.keys(quizAnswers).length > 0 ? quizAnswers : undefined,
        quizScore: Object.keys(quizAnswers).length > 0 ? getScore() : undefined,
        userEmail: user?.email || propInvitationEmail || undefined,
        userToken: inviteToken || undefined,
        userName: user?.fullName || undefined,
        company: user?.company || undefined
      };

      const token = localStorage.getItem('authToken');
      const response = await fetch(apiUrl('/api/module-submissions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitMessage('✅ Your challenges have been submitted successfully! We\'ll review them and get back to you soon.');
        // Clear the form
        setUserChallenges(['', '', '']);
      } else {
        setSubmitMessage(`❌ Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error submitting challenges:', error);
      setSubmitMessage('❌ Error submitting your challenges. Please try again.');
    } finally {
      setIsSubmittingChallenges(false);
    }
  };

  // Section completion functionality removed - not currently used

  // Helper function to extract first name from full name
  const getFirstName = (fullName: string | null | undefined): string => {
    if (!fullName) return '';
    const nameParts = fullName.trim().split(/\s+/);
    return nameParts[0] || '';
  };

  const renderSection = () => {
    if (currentSection >= moduleConfig.sections.length) {
      return null;
    }
    
    const section = moduleConfig.sections[currentSection];
    console.log('🔍 renderSection:', {
      currentSection,
      sectionId: section.id,
      sectionTitle: section.title,
      sectionContent: section.content,
      totalSections: moduleConfig.sections.length
    });
    
    const IconComponent = section.icon;
    
    // Handle different content types
    switch (section.content) {
      case 'beforeStart':
        // Before You Start section with PDF teaser
        console.log('🔍 beforeStart section rendering:', {
          hasPdfTeaser: !!moduleConfig.pdfTeaser,
          hasResources: !!moduleConfig.resources,
          resources: moduleConfig.resources,
          pdfTeaser: moduleConfig.pdfTeaser
        });
        
        if (!moduleConfig.pdfTeaser || !moduleConfig.resources || moduleConfig.resources.length === 0) {
          console.warn('⚠️ beforeStart: Missing pdfTeaser or resources config');
          return null; // Skip if no PDF teaser config or resources
        }
        
        const pdfResources = moduleConfig.resources.filter(r => r.downloadable && r.type === 'pdf');
        if (!pdfResources || pdfResources.length === 0) {
          console.warn('⚠️ beforeStart: No PDF resource found', {
            resources: moduleConfig.resources,
            pdfResources: moduleConfig.resources.filter(r => r.type === 'pdf')
          });
          return null;
        }
        
        // For modules with multiple PDFs (like Module 1), use the first one for the teaser
        // The download handler can be updated to download all PDFs if needed
        const pdfResource = pdfResources[0];
        
        // Prepare PDF resources for the modal (if multiple PDFs)
        const pdfResourcesForModal = pdfResources.length > 1 
          ? pdfResources.map(r => ({ title: r.title, url: r.url }))
          : undefined;
        
        console.log('✅ beforeStart: Rendering PDFTeaser with resource:', pdfResource);
        
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <IconComponent className="section-icon" />
              <h2>{section.title}</h2>
            </div>
            
            <div className="content-body">
              <PDFTeaser
                moduleId={moduleId}
                pdfFilename={pdfResource.url}
                pdfResources={pdfResourcesForModal}
                title={moduleConfig.pdfTeaser.title}
                subtitle={moduleConfig.pdfTeaser.subtitle}
                description={moduleConfig.pdfTeaser.description}
                urgency={moduleConfig.pdfTeaser.urgency}
                teasers={moduleConfig.pdfTeaser.teasers}
                socialProof={moduleConfig.pdfTeaser.socialProof}
                ctaText={moduleConfig.pdfTeaser.ctaText}
                onDownload={handleResourceDownload}
                onDownloadMultiple={handleMultipleResourceDownload}
                userId={user?.id}
                userTier={user?.userType || 'free'}
              />
            </div>
          </div>
        );
        
      case 'welcome':
        const firstName = getFirstName(user?.fullName);
        const welcomeGreeting = firstName ? `Hi, ${firstName},` : `Hi, welcome to module ${moduleId}`;
        
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <IconComponent className="section-icon book-icon" />
              <h2>{section.title}</h2>
            </div>
            
            <div className="welcome-content">
              <div className="instructor-intro user-section">
                <Users className="avatar-icon user-icon" />
                <div className="instructor-info user-content">
                  <h3 className="user-name">{welcomeGreeting}</h3>
                  <p className="instructor-bio user-description">
                    {moduleId === 1 ? (
                      <>
                        Spending over 20 years helping tech, fintech and other companies find the best senior IT talent worldwide, it's important to understand why Principal IT Talent matters. 
                        In this module, you'll learn why these professionals are so critical and how to tackle the unique challenges of hiring them. 
                        My goal is to share practical insights from my career so you can find top talent—because there's always a way if you know where to look and understand the market.
                      </>
                    ) : moduleId === 2 ? (
                      <>
                        Over two decades recruiting for cybersecurity firms, fintech companies, and high-tech startups, I've learned what sets principal IT talent apart.
                        In this module, you'll understand the traits, needs, and roles of principal candidates in the Czech and US markets.
                        By defining these clearly, you'll attract the right seniors - like the specialists I sourced for cybersecurity firms
                        through niche tech forums or the C++ developers I found for high-tech companies by digging beyond LinkedIn.
                      </>
                    ) : moduleId === 3 ? (
                      <>
                        With over two decades recruiting for tech companies, I've learned that finding principal IT talent means going beyond LinkedIn and job boards. 
                        In this module, you'll master proactive sourcing for passive seniors in the Czech and US markets - where 63% and 74% of firms face shortages, respectively. 
                        I've sourced rare talent through niche forums and via developer communities.
                      </>
                    ) : moduleId === 4 ? (
                      <>
                        Over two decades of tech recruiting, we've seen how a strong employer brand can pull in principal IT talent. 
                        In the Czech Republic, where 63% of firms face senior shortages, and the US, where 74% struggle, a compelling career page or tech blog can make all the difference. 
                        We've sourced rare talent through niche forums and via community engagement - by helping companies showcase what seniors value. 
                        Let's learn how to make your brand irresistible to top IT talent in CZ and US markets.
                      </>
                    ) : moduleId === 5 ? (
                      <>
                        Hi, welcome to module 5, and over two decades recruiting for tech, fintech or crypto companies, we've learned that securing principal IT talent requires sharp screening and personalised engagement. 
                        In the Czech Republic, where 63% of firms face senior shortages, and the US, where 74% struggle, generic ATS filters and interviews won't cut it. 
                        Let's master how to filter and engage seniors in CZ and US markets to land top hires.
                      </>
                    ) : moduleId === 6 ? (
                      <>
                        Over two decades recruiting for tech companies, we've learned that a proactive talent pipeline is the key to fast, high-quality senior hires. 
                        In the Czech Republic, where 63% of firms face senior shortages, and the US, where 74% struggle, waiting for applicants wastes time—often 60+ days per hire. 
                        We've built pipelines that delivered enabling a key hire in just 30 days. Let's master how to create and nurture a pipeline for principal IT talent in CZ and US markets.
                      </>
                    ) : moduleId === 7 ? (
                      <>
                        Over two decades recruiting for tech companies we've also learned that tracking the right metrics transforms hiring. 
                        In the Czech Republic, where senior hires take 90 days on average, and the US, where 74% of firms face shortages, data-driven insights cut time and boost quality. 
                        We've optimised pipelines by measuring what works. Let's master how to track and improve sourcing for principal IT talent in CZ and US markets.
                      </>
                    ) : moduleId === 8 ? (
                      <>
                        After guiding you through sourcing, screening, and optimising recruitment for senior IT talent, it's time to tackle your specific challenges and commit to action. 
                        During our path, we've faced tough questions—like how to compete with competitors in CZ or Big Tech in the US—and found solutions, like sourcing rare talent via niche forums or through targeted outreach.
                      </>
                    ) : (
                      <>
                        Over two decades recruiting for cybersecurity firms, fintech companies, and high-tech startups, I've learned what sets principal IT talent apart.
                        In this module, you'll understand the traits, needs, and roles of principal candidates in the Czech and US markets.
                        By defining these clearly, you'll attract the right seniors - like the specialists I sourced for cybersecurity firms
                        through niche tech forums or the C++ developers I found for high-tech companies by digging beyond LinkedIn.
                      </>
                    )}
                  </p>
                  <p className="instructor-bio user-description" style={{ fontWeight: 600 }}>
                    {moduleId === 1 ? (
                      <>My goal is to share practical insights from my career so you can find top talent—because there's always a way if you know where to look and understand the market.</>
                    ) : moduleId === 2 ? (
                      <>Let's unlock how to pinpoint and engage top IT talent, because when you know your stuff, there's always a way to find them.</>
                    ) : moduleId === 3 ? (
                      <></>
                    ) : moduleId === 4 ? (
                      <>Let's learn how to make your brand irresistible to top IT talent in CZ and US markets.</>
                    ) : moduleId === 5 ? (
                      <>Let's master how to filter and engage seniors in CZ and US markets to land top hires.</>
                    ) : moduleId === 6 ? (
                      <>Let's master how to create and nurture a pipeline for principal IT talent in CZ and US markets.</>
                    ) : moduleId === 7 ? (
                      <>Let's master how to track and improve sourcing for principal IT talent in CZ and US markets.</>
                    ) : moduleId === 8 ? (
                      <>Let's master how to create and execute a 30-day action plan to transform your hiring.</>
                    ) : (
                      <>Let's unlock how to pinpoint and engage top IT talent, because when you know your stuff, there's always a way to find them.</>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="module-objectives">
                <h4>What You'll Learn:</h4>
                <ul>
                  {moduleId === 1 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Why principal IT talent is crucial for innovation</li>
                      <li><CheckCircle className="check-icon" /> Czech and US market challenges and opportunities</li>
                      <li><CheckCircle className="check-icon" /> Creative sourcing strategies beyond LinkedIn</li>
                      <li><CheckCircle className="check-icon" /> Real-world examples from top tech companies</li>
                    </>
                  ) : moduleId === 2 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Key characteristics of principal IT professionals</li>
                      <li><CheckCircle className="check-icon" /> What motivates senior talent in CZ vs US markets</li>
                      <li><CheckCircle className="check-icon" /> Common roles in high demand across both markets</li>
                      <li><CheckCircle className="check-icon" /> How to write job descriptions that attract principals</li>
                    </>
                  ) : moduleId === 3 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> LinkedIn Recruiter Boolean search strategies</li>
                      <li><CheckCircle className="check-icon" /> GitHub engagement for passive talent</li>
                      <li><CheckCircle className="check-icon" /> Tech community networking tactics</li>
                      <li><CheckCircle className="check-icon" /> AI tools for automated sourcing</li>
                      <li><CheckCircle className="check-icon" /> Referral program best practices</li>
                    </>
                  ) : moduleId === 4 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Showcase tech stacks (e.g., React, AWS) and project impact</li>
                      <li><CheckCircle className="check-icon" /> Optimise career pages to attract senior talent (job boards like Jobs.cz don't effectively reach principals)</li>
                      <li><CheckCircle className="check-icon" /> Publish tech blogs and speak at Prague Tech Fest or TechCrunch Disrupt</li>
                      <li><CheckCircle className="check-icon" /> Build trust with senior engineer testimonials</li>
                    </>
                  ) : moduleId === 5 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Screen with Codility (CZ) or HackerRank (US) and GitHub portfolios</li>
                      <li><CheckCircle className="check-icon" /> Personalise outreach (e.g., "Your TensorFlow project fits our AI role")</li>
                      <li><CheckCircle className="check-icon" /> Avoid pitfalls like over-relying on ATS filters</li>
                      <li><CheckCircle className="check-icon" /> Offer flexible interviews to accommodate senior schedules</li>
                      <li><CheckCircle className="check-icon" /> Balance screening: skills (40%), experience (30%), leadership/culture (30%)</li>
                    </>
                  ) : moduleId === 6 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Build databases of 20–50 pre-vetted senior candidates</li>
                      <li><CheckCircle className="check-icon" /> Use Airtable or Notion for pipeline tracking</li>
                      <li><CheckCircle className="check-icon" /> Nurture relationships with monthly check-ins and engagement</li>
                      <li><CheckCircle className="check-icon" /> CZ-specific strategies: Prague/Brno sourcing and expat communities</li>
                      <li><CheckCircle className="check-icon" /> US-specific strategies: Tech hubs, remote candidates, and professional networks</li>
                    </>
                  ) : moduleId === 7 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Track key metrics: time-to-hire ({'<60'} days), offer acceptance ({'>80'}%), candidate quality (90%)</li>
                      <li><CheckCircle className="check-icon" /> Use LinkedIn Talent Insights, Google Analytics, and ATS dashboards</li>
                      <li><CheckCircle className="check-icon" /> Optimise strategies based on data: shift channels if quality drops</li>
                      <li><CheckCircle className="check-icon" /> CZ-specific optimisation: Jobs.cz alternatives, nurturing tactics</li>
                      <li><CheckCircle className="check-icon" /> US-specific optimisation: equity offers, faster responses, channel shifts</li>
                    </>
                  ) : moduleId === 8 ? (
                    <>
                      <li><CheckCircle className="check-icon" /> Address company-specific challenges: competing with big tech, attracting seniors, improving ATS filters</li>
                      <li><CheckCircle className="check-icon" /> Create a 30-day action plan with clear owners, deadlines, and expected outcomes</li>
                      <li><CheckCircle className="check-icon" /> CZ-specific actions: revise job descriptions, join Prague Devs, test GitHub outreach</li>
                      <li><CheckCircle className="check-icon" /> US-specific actions: update career pages with equity, network at events, implement AI tools</li>
                      <li><CheckCircle className="check-icon" /> Track progress and iterate: review metrics at day 30 to optimise strategies</li>
                    </>
                  ) : (
                    <>
                      <li><CheckCircle className="check-icon" /> Key characteristics of principal IT professionals</li>
                      <li><CheckCircle className="check-icon" /> What motivates senior talent in CZ vs US markets</li>
                      <li><CheckCircle className="check-icon" /> Common roles in high demand across both markets</li>
                      <li><CheckCircle className="check-icon" /> How to write job descriptions that attract principals</li>
                    </>
                  )}
                </ul>
              </div>

              <div className="module-meta module-info-grid">
                <div className="meta-item module-info-item">
                  <Clock className="info-icon" />
                  <span className="info-text">{moduleConfig.duration} (self-paced)</span>
                </div>
                <div className="meta-item module-info-item">
                  <Target className="info-icon" />
                  <span className="info-text">TA teams at Czech and US IT companies</span>
                </div>
                <div className="meta-item module-info-item">
                  <Award className="info-icon" />
                  <span className="info-text">Practical skills & knowledge</span>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'quiz':
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <IconComponent className="section-icon" />
              <h2>{section.title}</h2>
            </div>
            
            <div className="content-section">
              {!showQuizResults ? (
                <div className="quiz-container">
                  {moduleConfig.quizQuestions.map((question, index) => (
                    <div key={question.id} className="quiz-question">
                      <h4>Question {index + 1}: {question.question}</h4>
                      <div className="quiz-options">
                        {question.options.map((option, optionIndex) => (
                          <label key={optionIndex} className="quiz-option">
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={option}
                              onChange={() => handleQuizAnswer(question.id, option)}
                              checked={quizAnswers[question.id] === option}
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  <div className="quiz-actions">
                    <button 
                      className="submit-quiz-btn"
                      onClick={handleQuizSubmit}
                      disabled={Object.keys(quizAnswers).length < moduleConfig.quizQuestions.length}
                    >
                      Submit Quiz
                    </button>
                  </div>
                </div>
              ) : (
                <div className="quiz-results">
                  <div className="score-display">
                    <h3>Quiz Results</h3>
                    <div className="score">{getScore()}%</div>
                    <p>You got {Object.values(quizAnswers).filter((answer, index) => 
                      answer === moduleConfig.quizQuestions[index].options[moduleConfig.quizQuestions[index].correct]
                    ).length} out of {moduleConfig.quizQuestions.length} questions correct!</p>
                  </div>
                  
                  <div className="answer-review">
                    {moduleConfig.quizQuestions.map((question, index) => {
                      const userAnswer = quizAnswers[question.id];
                      const isCorrect = userAnswer === question.options[question.correct];
                      
                      return (
                        <div key={question.id} className="answer-item">
                          <h5>Question {index + 1}</h5>
                          <p>{question.question}</p>
                          <div className={`answer-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                            <div className="user-answer">
                              <span className="answer-label">Your answer:</span>
                              <span className="answer-value">{userAnswer}</span>
                            </div>
                            <div className="correct-answer">
                              <span className="answer-label">Correct answer:</span>
                              <span className="answer-value">{question.options[question.correct]}</span>
                            </div>
                            <div className="explanation">{question.explanation}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'activity':
        // Special handling for Module 1 Activity section
        if (moduleId === 1 && section.id === 4) {
          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <MessageSquare className="section-icon" />
                <h2>Reflect on Your Hiring Challenges</h2>
              </div>
              
              <div className="content-body">
                <p className="lead-text" style={{ marginBottom: '2rem', fontSize: '1.1rem', lineHeight: '1.6', color: 'var(--dark)' }}>
                  Take a moment to think about your biggest hurdles in hiring senior IT talent. What's stopping you from finding 
                  the right principal engineers or specialists? Examples might include: "Too many unqualified applicants," "Seniors prefer remote U.S. jobs," or "Our TA team lacks sourcing experience."
                </p>

                <div className="activity-container" style={{ marginTop: '2rem' }}>
                  <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e9ecef' }}>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={20} style={{ color: 'var(--orange)' }} />
                      Before You Start
                    </h4>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', marginBottom: '0.75rem' }}>
                      Make sure you've downloaded the <strong>Market Insights Hacker Kit</strong> (Czech + US Checklists) from the "Before You Start" section. 
                      Review the "Principal Talent Identification Checklist" and market stats to inform your reflection.
                    </p>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', margin: 0 }}>
                      <strong>Time:</strong> 10 minutes (self-paced)
                    </p>
                  </div>

                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '1.5rem' }}>
                    Write down your top 3 challenges:
                  </h4>
                  
                  {userChallenges.map((challenge, index) => (
                    <div key={index} className="challenge-input-group" style={{ marginBottom: '1.5rem' }}>
                      <label htmlFor={`challenge-${index}`} style={{ display: 'block', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                        Challenge {index + 1}:
                      </label>
                      <input
                        type="text"
                        id={`challenge-${index}`}
                        value={challenge}
                        onChange={(e) => handleChallengeChange(index, e.target.value)}
                        placeholder={[
                          "e.g., Too many unqualified applicants flooding our pipeline",
                          "e.g., Senior candidates ignore our job postings", 
                          "e.g., Competition from big tech companies like JetBrains"
                        ][index]}
                        className="challenge-input"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontFamily: 'var(--montserrat)',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  ))}
                  
                  <button 
                    className="challenge-submit-btn"
                    onClick={handleSubmitChallenges}
                    disabled={isSubmittingChallenges}
                    style={{
                      background: isSubmittingChallenges ? '#ccc' : 'var(--orange)',
                      color: 'white',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: isSubmittingChallenges ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      marginTop: '1rem'
                    }}
                    onMouseOver={(e) => {
                      if (!isSubmittingChallenges) {
                        e.currentTarget.style.background = '#d4a017';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSubmittingChallenges) {
                        e.currentTarget.style.background = 'var(--orange)';
                      }
                    }}
                  >
                    {isSubmittingChallenges ? 'Submitting...' : 'Submit My Challenges'}
                  </button>
                  
                  {submitMessage && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      borderRadius: '8px',
                      background: submitMessage.includes('✅') ? '#f0f9ff' : '#fef2f2',
                      border: `1px solid ${submitMessage.includes('✅') ? '#bae6fd' : '#fecaca'}`,
                      color: submitMessage.includes('✅') ? '#1e40af' : '#991b1b',
                      fontSize: '0.875rem'
                    }}>
                      {submitMessage}
                    </div>
                  )}
                  
                  <div className="activity-note activity-note-module1">
                    <Lock size={18} className="note-icon" style={{ color: 'var(--orange)' }} />
                    <span>Your input will be sent to us for optional personalised feedback. We'll review your challenges and offer tailored tips to help you overcome these hurdles.</span>
                  </div>

                  <div className="sample-challenges-box">
                    <h4>Sample Challenges (to spark ideas):</h4>
                    <ol>
                      <li>Too many applicants, but few with principal-level skills.</li>
                      <li>Seniors are drawn to better-paying remote roles abroad.</li>
                      <li>Our TA team struggles to source beyond LinkedIn or Jobs.cz.</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 2 Activity section
        if (moduleId === 2 && section.id === 4) {
          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>Interactive Task (you need the PDF)</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Open the kit → complete the 25–30 min "Prep for Success" intake form → submit async for optional feedback.</p>
                      <p>Define a principal IT role for your company (e.g., Senior Backend Engineer, Principal AI Specialist). List 5 must-have skills and 3 key motivators specific to your market (CZ or US).</p>
                      <p><strong>Example:</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0' }}>
                        <li><strong>Role:</strong> Senior Backend Engineer</li>
                        <li><strong>Skills:</strong> Go, Kubernetes, REST APIs, microservices, SQL</li>
                        <li><strong>Motivators:</strong> Remote work, impactful SaaS projects, €1,000 learning budget</li>
                      </ul>
                      <p>Submit your data below. I'll review it and offer tailored feedback to refine your hiring strategy.</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Sample Worksheet</h3>
                      <p><strong>Role:</strong> Principal Cloud Architect (Data Analytics Company, US)</p>
                      <p><strong>Skills:</strong> AWS, Terraform, distributed systems, Python, CI/CD pipelines</p>
                      <p><strong>Motivators:</strong> 0.2% equity, hybrid work, leading enterprise migrations</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Submission & Purpose</h3>
                      <p><strong>Submission:</strong> Use the form below to submit your role definition.</p>
                      <p><strong>Time:</strong> 10 minutes (self-paced).</p>
                      <p><strong>Purpose:</strong> Helps TA teams clarify role requirements and align with candidate motivators, addressing undertraining in CZ and Big Tech competition in the US.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-form">
                  <h3>Submit Your Role Definition</h3>
                  <p>All fields are optional, but please provide at least one field so we can offer personalized feedback.</p>
                  <form onSubmit={handleRoleDefinitionSubmit}>
                    <div className="form-field-group">
                      <label htmlFor="role" className="form-label">Role</label>
                      <input 
                        type="text" 
                        id="role" 
                        name="role" 
                        value={roleDefinitionForm.role}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Senior Backend Engineer, Principal AI Specialist"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="skills" className="form-label">Skills</label>
                      <textarea 
                        id="skills" 
                        name="skills" 
                        value={roleDefinitionForm.skills}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, skills: e.target.value }))}
                        placeholder="List 5 must-have skills (e.g., Go, Kubernetes, REST APIs, microservices, SQL)"
                        rows={3}
                        className="form-textarea role-definition-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="motivators" className="form-label">Motivators</label>
                      <textarea 
                        id="motivators" 
                        name="motivators" 
                        value={roleDefinitionForm.motivators}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, motivators: e.target.value }))}
                        placeholder="List 3 key motivators (e.g., Remote work, impactful SaaS projects, €1,000 learning budget)"
                        rows={3}
                        className="form-textarea role-definition-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="company-name" className="form-label">Company Name</label>
                      <input 
                        type="text" 
                        id="company-name" 
                        name="companyName" 
                        value={roleDefinitionForm.companyName}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, companyName: e.target.value }))}
                        placeholder="e.g., TechCorp Inc."
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="company-description" className="form-label">Company Description</label>
                      <textarea 
                        id="company-description" 
                        name="companyDescription" 
                        value={roleDefinitionForm.companyDescription}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, companyDescription: e.target.value }))}
                        placeholder="Brief description of your company"
                        rows={3}
                        className="form-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="company-link" className="form-label">Company Link</label>
                      <input 
                        type="text" 
                        id="company-link" 
                        name="companyLink" 
                        value={roleDefinitionForm.companyLink}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, companyLink: e.target.value }))}
                        placeholder="https://yourcompany.com"
                        className="form-input"
                        inputMode="url"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="jd-link" className="form-label">JD Link</label>
                      <input 
                        type="text" 
                        id="jd-link" 
                        name="jdLink" 
                        value={roleDefinitionForm.jdLink}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, jdLink: e.target.value }))}
                        placeholder="https://yourcompany.com/jobs/role-description"
                        className="form-input"
                        inputMode="url"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="jd-text" className="form-label">JD Text</label>
                      <textarea 
                        id="jd-text" 
                        name="jdText" 
                        value={roleDefinitionForm.jdText}
                        onChange={(e) => setRoleDefinitionForm(prev => ({ ...prev, jdText: e.target.value }))}
                        placeholder="Paste your job description text here"
                        rows={6}
                        className="form-textarea"
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingRoleDefinition}
                      className="form-submit-btn"
                    >
                      {isSubmittingRoleDefinition ? 'Submitting...' : 'Submit Role Definition'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 3 Activity section (Hands-On Sourcing Practice)
        if (moduleId === 3 && section.id === 6) {
          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>Hands-On Activity (you need the PDF)</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Open the kit → complete the 30-min Prep (fill Target Candidate Profile for a role) + 45-min Strategies (test 1 Boolean + draft GitHub message) → submit async for optional feedback.</p>
                      <p><strong>Earn the "Passive Talent Hunter" badge.</strong></p>
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <p style={{ marginBottom: '0.75rem', fontWeight: '600', color: 'var(--dark)' }}>Before You Start:</p>
                        <p style={{ marginBottom: '0.5rem', color: 'var(--gray)' }}>Make sure you've downloaded the <strong>Sourcing Hacker Kit (Cheat Sheet Basics)</strong> from the "Before You Start" section.</p>
                        <p style={{ color: 'var(--gray)', margin: 0 }}>Review the "Prep for Sourcing" and "Sourcing Strategies" sections in the PDF to guide your work.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>What You'll Complete</h3>
                      <p><strong>Part 1 - Target Candidate Profile (30 min):</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0', marginBottom: '1rem' }}>
                        <li>Define a principal IT role you're hiring for</li>
                        <li>List 5 must-have skills</li>
                        <li>Identify 3 key motivators (CZ: hybrid/learning; US: equity/projects)</li>
                        <li>Select primary sourcing channels (CZ: Jobs.cz/LinkedIn/GitHub/Prague Devs; US: LinkedIn/StackOverflow/IEEE)</li>
                      </ul>
                      <p><strong>Part 2 - Sourcing Strategies (45 min):</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0' }}>
                        <li>Test 1 Boolean search template from the PDF (e.g., CZ: "senior AND (AI OR cybersecurity OR devops) AND Prague -junior")</li>
                        <li>Draft a personalised GitHub outreach message</li>
                        <li>Note which community you'd engage (Prague Devs, TechCrunch Disrupt, etc.)</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Submission & Purpose</h3>
                      <p><strong>Time:</strong> 75 minutes total (30 min Prep + 45 min Strategies, self-paced)</p>
                      <p><strong>Purpose:</strong> Helps TA teams apply sourcing basics to find passive seniors, addressing the 90% passive challenge and achieving {'>'}20% response rates.</p>
                      <p style={{ marginTop: '0.75rem', color: 'var(--gray)', fontStyle: 'italic' }}>Your input will be sent to us for optional personalised feedback. We'll review your profile and strategies to help you refine your sourcing approach.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-note" style={{
                  marginTop: '2rem',
                  padding: '1rem',
                  background: 'rgba(216, 183, 37, 0.1)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: 'var(--dark)',
                  fontSize: '0.9rem'
                }}>
                  <Lock size={18} style={{ color: 'var(--orange)' }} />
                  <span>Submit your completed activity below. I'll review it as admin and offer tailored feedback (optional) to help you source hidden talent effectively.</span>
                </div>
                
                <div className="activity-form">
                  <h3>Submit Your Sourcing Practice</h3>
                  <form onSubmit={handleSourcingPracticeSubmit}>
                    <div className="form-field-group">
                      <label htmlFor="sourcing-role" className="form-label">Principal IT Role <span className="form-label-required">*</span></label>
                      <input 
                        type="text" 
                        id="sourcing-role" 
                        name="role" 
                        value={sourcingPracticeForm.role}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Senior Backend Engineer, Principal AI Specialist"
                        required
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="sourcing-skills" className="form-label">5 Must-Have Skills <span className="form-label-required">*</span></label>
                      <textarea 
                        id="sourcing-skills" 
                        name="skills" 
                        value={sourcingPracticeForm.skills}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, skills: e.target.value }))}
                        placeholder="List 5 must-have skills (e.g., Go, Kubernetes, REST APIs, microservices, SQL)"
                        required
                        rows={3}
                        className="form-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="sourcing-motivators" className="form-label">3 Key Motivators <span className="form-label-required">*</span></label>
                      <textarea 
                        id="sourcing-motivators" 
                        name="motivators" 
                        value={sourcingPracticeForm.motivators}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, motivators: e.target.value }))}
                        placeholder="List 3 key motivators (CZ: hybrid/learning; US: equity/projects)"
                        required
                        rows={3}
                        className="form-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="sourcing-channels" className="form-label">Primary Sourcing Channels <span className="form-label-required">*</span></label>
                      <textarea 
                        id="sourcing-channels" 
                        name="sourcingChannels" 
                        value={sourcingPracticeForm.sourcingChannels}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, sourcingChannels: e.target.value }))}
                        placeholder="CZ: Jobs.cz/LinkedIn/GitHub/Prague Devs; US: LinkedIn/StackOverflow/IEEE"
                        required
                        rows={2}
                        className="form-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="boolean-search" className="form-label">Boolean Search Template <span className="form-label-required">*</span></label>
                      <textarea 
                        id="boolean-search" 
                        name="booleanSearch" 
                        value={sourcingPracticeForm.booleanSearch}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, booleanSearch: e.target.value }))}
                        placeholder='e.g., CZ: "senior AND (AI OR cybersecurity OR devops) AND Prague -junior"'
                        required
                        rows={2}
                        className="form-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="github-message" className="form-label">GitHub Outreach Message <span className="form-label-required">*</span></label>
                      <textarea 
                        id="github-message" 
                        name="githubMessage" 
                        value={sourcingPracticeForm.githubMessage}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, githubMessage: e.target.value }))}
                        placeholder="Draft a personalised GitHub outreach message"
                        required
                        rows={4}
                        className="form-textarea"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="community" className="form-label">Community to Engage <span className="form-label-required">*</span></label>
                      <input 
                        type="text" 
                        id="community" 
                        name="community" 
                        value={sourcingPracticeForm.community}
                        onChange={(e) => setSourcingPracticeForm(prev => ({ ...prev, community: e.target.value }))}
                        placeholder="e.g., Prague Devs, TechCrunch Disrupt, IEEE"
                        required
                        className="form-input"
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingSourcingPractice}
                      className="form-submit-btn"
                    >
                      {isSubmittingSourcingPractice ? 'Submitting...' : 'Submit Sourcing Practice'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 4 Activity section (Employer Brand Audit)
        if (moduleId === 4 && section.id === 4) {
          const handleAuditSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingAudit(true);

            try {
              const submission = {
                careerPageUrl: normalizeUrl(employerBrandAuditForm.careerPageUrl),
                improvements: employerBrandAuditForm.improvements,
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 4
              };

              const response = await fetch(apiUrl('/api/module-submissions/employer-brand-audit-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowEmployerBrandAuditSuccessModal(true);
                setEmployerBrandAuditForm({ careerPageUrl: '', improvements: '' });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting audit:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingAudit(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>{section.title}</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Open the kit → complete 30-min Prep (fill Brand Audit Form for your company) + 25-30 min Strategies (pick 3 tips to apply) → submit async for optional feedback.</p>
                      <p><strong>Earn the "Brand Magnet" badge.</strong></p>
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <p style={{ marginBottom: '0.75rem', fontWeight: '600', color: 'var(--dark)' }}>Before You Start:</p>
                        <p style={{ marginBottom: '0.5rem', color: 'var(--gray)' }}>Make sure you've downloaded the <strong>Branding Hacker Kit (Checklist)</strong> from the "Before You Start" section.</p>
                        <p style={{ color: 'var(--gray)', margin: 0 }}>Review the "Prep for Branding" and "Branding Strategies" sections in the PDF to guide your work.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>What You'll Complete</h3>
                      <p><strong>Part 1 - Brand Audit Form (30 min):</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0', marginBottom: '1rem' }}>
                        <li>Fill out the Brand Audit Form from the PDF (perception, feedback, gaps, channels)</li>
                        <li>Answer Stakeholder Questions (marketing/leadership/employees/HR)</li>
                        <li>Identify key gaps in your current branding</li>
                      </ul>
                      <p><strong>Part 2 - Branding Strategies (25-30 min):</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0' }}>
                        <li>Pick 3 tips from the PDF to apply to your company</li>
                        <li>Focus on tech stacks, impact, culture/autonomy (CZ: hybrid/learning; US: equity/projects)</li>
                        <li>Note specific improvements for your career page or platforms</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Submission & Purpose</h3>
                      <p><strong>Time:</strong> 55-60 minutes total (30 min Prep + 25-30 min Strategies, self-paced)</p>
                      <p><strong>Purpose:</strong> Helps TA teams audit and fix their brand to attract seniors, turning your company into a magnet for {'>'}20% more qualified seniors.</p>
                      <p style={{ marginTop: '0.75rem', color: 'var(--gray)', fontStyle: 'italic' }}>Your input will be sent to us for optional personalised feedback. We'll review your audit and strategies to help you refine your branding approach.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-note" style={{
                  marginTop: '2rem',
                  padding: '1rem',
                  background: 'rgba(216, 183, 37, 0.1)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: 'var(--dark)',
                  fontSize: '0.9rem'
                }}>
                  <Lock size={18} style={{ color: 'var(--orange)' }} />
                  <span>Submit your completed activity below. I'll review it as admin and offer tailored feedback (optional) to help you turn your brand into a senior magnet.</span>
                </div>
                
                <div className="activity-form">
                  <h3>Submit Your Brand Audit & Strategies</h3>
                  <form onSubmit={handleAuditSubmit}>
                    <div className="form-field-group">
                      <label htmlFor="career-page-url" className="form-label">Career Page URL <span className="form-label-required">*</span></label>
                      <input 
                        type="text" 
                        id="career-page-url" 
                        name="careerPageUrl" 
                        value={employerBrandAuditForm.careerPageUrl}
                        onChange={(e) => setEmployerBrandAuditForm(prev => ({ ...prev, careerPageUrl: e.target.value }))}
                        placeholder="https://yourcompany.com/careers"
                        required
                        className="form-input"
                        inputMode="url"
                      />
                    </div>
                    
                    <div className="form-field-group">
                      <label htmlFor="improvements" className="form-label">3 Tips/Improvements from PDF (Part 2) <span className="form-label-required">*</span></label>
                      <textarea 
                        id="improvements" 
                        name="improvements" 
                        value={employerBrandAuditForm.improvements}
                        onChange={(e) => setEmployerBrandAuditForm(prev => ({ ...prev, improvements: e.target.value }))}
                        placeholder="List 3 tips from the PDF you'll apply:&#10;1. Add React and AWS details to tech stack section&#10;2. Highlight remote work options (70% CZ preference)&#10;3. Include a senior engineer testimonial video"
                        required
                        rows={6}
                        className="form-textarea"
                      />
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.25rem' }}>Based on your Brand Audit Form (Part 1) and the 3 tips you selected from the PDF (Part 2).</p>
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingAudit}
                      className="form-submit-btn"
                    >
                      {isSubmittingAudit ? 'Submitting...' : 'Submit Brand Audit & Strategies'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 5 Activity section (Screening and Engagement Role-Play)
        if (moduleId === 5 && section.id === 4) {
          const handleScreeningEngagementSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingScreening(true);

            try {
              const submission = {
                role: screeningEngagementForm.role,
                email: screeningEngagementForm.email,
                question1: screeningEngagementForm.question1,
                question2: screeningEngagementForm.question2,
                question3: screeningEngagementForm.question3,
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 4
              };

              const response = await fetch(apiUrl('/api/module-submissions/screening-engagement-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowScreeningEngagementSuccessModal(true);
                setScreeningEngagementForm({ role: '', email: '', question1: '', question2: '', question3: '' });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting screening engagement:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingScreening(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>{section.title}</h2>
              </div>
              
              <div className="content-body">
                <p className="lead-text">
                  Practice screening and engaging a principal IT candidate (e.g., Senior DevOps Engineer) for your company in CZ or US. Complete two tasks:
                </p>
                
                <ol style={{ marginBottom: '2rem', paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li style={{ marginBottom: '1rem' }}>Craft a personalised outreach email (e.g., for a GitHub contributor, under 100 words).</li>
                  <li style={{ marginBottom: '1rem' }}>Write 3 mock-interview questions for a "senior candidate" (e.g., technical, leadership, cultural fit).</li>
                </ol>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(216, 183, 37, 0.1) 0%, rgba(4, 39, 137, 0.1) 100%)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  marginBottom: '2rem',
                  borderLeft: '4px solid var(--orange)'
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Sample Submission:</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontFamily: 'var(--raleway)' }}>
                    <p style={{ margin: 0 }}><strong>Role:</strong> Senior DevOps Engineer (Company Name, CZ)</p>
                    <p style={{ margin: 0 }}><strong>Email:</strong> "Hi Alex, your Kubernetes repo on GitHub aligns with our cloud platform at [company_name]. Interested in a lead role with hybrid work and €1K learning budget? Let's chat. Regards, [Your Name]."</p>
                    <div style={{ marginTop: '0.5rem' }}>
                      <p style={{ margin: '0.5rem 0', fontWeight: '600' }}>Questions:</p>
                      <ol style={{ margin: 0, paddingLeft: '1.5rem' }}>
                        <li style={{ marginBottom: '0.5rem' }}>Technical: "How did you optimise a CI/CD pipeline in your last project?"</li>
                        <li style={{ marginBottom: '0.5rem' }}>Leadership: "Describe a time you mentored a team through a cloud migration."</li>
                        <li style={{ marginBottom: '0.5rem' }}>Cultural Fit: "What motivates you to join a collaborative, hybrid team?"</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleScreeningEngagementSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '800px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label htmlFor="role" style={{ fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Role <span style={{ color: '#dc2626' }}>*</span></label>
                    <input 
                      type="text" 
                      id="role" 
                      name="role" 
                      value={screeningEngagementForm.role}
                      onChange={(e) => setScreeningEngagementForm(prev => ({ ...prev, role: e.target.value }))}
                      placeholder="e.g., Senior DevOps Engineer (Company Name, CZ)"
                      required
                      style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label htmlFor="email" style={{ fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Personalised Outreach Email <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea 
                      id="email" 
                      name="email" 
                      value={screeningEngagementForm.email}
                      onChange={(e) => setScreeningEngagementForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Hi [Name], your [specific work] aligns with our [project/role] at [company]. Interested in a [role type] with [benefits]? Let's chat. Regards, [Your Name]."
                      required
                      rows={6}
                      style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                    />
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray)', margin: 0 }}>Keep it under 100 words and reference specific work (e.g., GitHub commits).</p>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label htmlFor="question1" style={{ fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Question 1: Technical <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea 
                      id="question1" 
                      name="question1" 
                      value={screeningEngagementForm.question1}
                      onChange={(e) => setScreeningEngagementForm(prev => ({ ...prev, question1: e.target.value }))}
                      placeholder="e.g., How did you optimise a CI/CD pipeline in your last project?"
                      required
                      rows={3}
                      style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label htmlFor="question2" style={{ fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Question 2: Leadership <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea 
                      id="question2" 
                      name="question2" 
                      value={screeningEngagementForm.question2}
                      onChange={(e) => setScreeningEngagementForm(prev => ({ ...prev, question2: e.target.value }))}
                      placeholder="e.g., Describe a time you mentored a team through a cloud migration."
                      required
                      rows={3}
                      style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label htmlFor="question3" style={{ fontWeight: '600', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Question 3: Cultural Fit <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea 
                      id="question3" 
                      name="question3" 
                      value={screeningEngagementForm.question3}
                      onChange={(e) => setScreeningEngagementForm(prev => ({ ...prev, question3: e.target.value }))}
                      placeholder="e.g., What motivates you to join a collaborative, hybrid team?"
                      required
                      rows={3}
                      style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                    />
                  </div>
                  
                  <div style={{ 
                    background: '#f0f9ff',
                    padding: '1rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: 'var(--gray)',
                    fontFamily: 'var(--raleway)'
                  }}>
                    <p style={{ margin: 0, fontWeight: '600', color: 'var(--dark)' }}>Time:</p>
                    <p style={{ margin: '0.25rem 0 0 0' }}>20 minutes (self-paced)</p>
                    <p style={{ margin: '1rem 0 0.25rem 0', fontWeight: '600', color: 'var(--dark)' }}>Purpose:</p>
                    <p style={{ margin: 0 }}>Builds practical skills for undertrained CZ TA teams and US teams facing Big Tech competition.</p>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={isSubmittingScreening}
                    style={{
                      background: isSubmittingScreening ? '#ccc' : 'var(--orange)',
                      color: 'white',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: isSubmittingScreening ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      alignSelf: 'flex-start'
                    }}
                    onMouseOver={(e) => {
                      if (!isSubmittingScreening) {
                        e.currentTarget.style.background = '#d4a017';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSubmittingScreening) {
                        e.currentTarget.style.background = 'var(--orange)';
                      }
                    }}
                  >
                    {isSubmittingScreening ? 'Submitting...' : 'Submit Practice'}
                  </button>
                </form>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 8 Activity section (30-Day Action Plan)
        if (moduleId === 8 && section.id === 3) {
          const handleActionPlanSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingActionPlan(true);

            try {
              const submission = {
                role: actionPlanForm.role,
                actions: [
                  {
                    action: actionPlanForm.action1,
                    owner: actionPlanForm.owner1,
                    deadline: actionPlanForm.deadline1,
                    outcome: actionPlanForm.outcome1
                  },
                  {
                    action: actionPlanForm.action2,
                    owner: actionPlanForm.owner2,
                    deadline: actionPlanForm.deadline2,
                    outcome: actionPlanForm.outcome2
                  },
                  {
                    action: actionPlanForm.action3,
                    owner: actionPlanForm.owner3,
                    deadline: actionPlanForm.deadline3,
                    outcome: actionPlanForm.outcome3
                  }
                ],
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 3
              };

              const response = await fetch(apiUrl('/api/module-submissions/action-plan-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowActionPlanSuccessModal(true);
                setActionPlanForm({
                  role: '',
                  action1: '', owner1: '', deadline1: '', outcome1: '',
                  action2: '', owner2: '', deadline2: '', outcome2: '',
                  action3: '', owner3: '', deadline3: '', outcome3: ''
                });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting action plan:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingActionPlan(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>{section.title}</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Create a 30-day action plan for hiring a principal IT role (e.g., Senior AI Engineer) in CZ or US. List 3 actions with owners, deadlines, and expected outcomes. Submit below. We'll review and offer tailored feedback (optional).</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Sample Action Plan</h3>
                      <p><strong>Role:</strong> Senior DevOps Engineer (Travel Tech Company, CZ)</p>
                      <div style={{ marginTop: '1rem' }}>
                        <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Actions:</p>
                        <ol style={{ marginLeft: '1.5rem', paddingLeft: '0', lineHeight: '1.8' }}>
                          <li><strong>Action:</strong> Redesign career page to highlight Kubernetes and hybrid work. <strong>Owner:</strong> TA Lead. <strong>Deadline:</strong> Week 1. <strong>Outcome:</strong> 20% more senior applicants.</li>
                          <li><strong>Action:</strong> Join Prague Devs meetup to source candidates. <strong>Owner:</strong> Recruiter. <strong>Deadline:</strong> Week 2. <strong>Outcome:</strong> 5 senior contacts added to pipeline.</li>
                          <li><strong>Action:</strong> Test GitHub outreach for Kubernetes experts. <strong>Owner:</strong> TA Manager. <strong>Deadline:</strong> Week 3. <strong>Outcome:</strong> 2 senior candidates shortlisted.</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Time & Purpose</h3>
                      <p><strong>Time:</strong> 10 minutes (self-paced).</p>
                      <p><strong>Purpose:</strong> Commits TA teams to actionable steps, addressing CZ's undertrained teams and US's competitive hiring.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-form">
                  <h3>Submit Your 30-Day Action Plan</h3>
                  <form onSubmit={handleActionPlanSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="role-action" style={{ fontWeight: '600', color: 'var(--dark)' }}>Role <span style={{ color: '#dc2626' }}>*</span></label>
                      <input 
                        type="text" 
                        id="role-action" 
                        name="role" 
                        value={actionPlanForm.role}
                        onChange={(e) => setActionPlanForm(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Senior DevOps Engineer (Travel Tech Company, CZ)"
                        required
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                      />
                    </div>
                    
                    {[1, 2, 3].map((num) => (
                      <div key={num} style={{ 
                        padding: '1.5rem', 
                        background: 'white', 
                        borderRadius: '8px', 
                        border: '1px solid #e9ecef',
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '1rem' 
                      }}>
                        <h4 style={{ margin: 0, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Action {num}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label htmlFor={`action${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Action <span style={{ color: '#dc2626' }}>*</span></label>
                          <textarea 
                            id={`action${num}`}
                            name={`action${num}`}
                            value={actionPlanForm[`action${num}` as keyof typeof actionPlanForm]}
                            onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`action${num}`]: e.target.value }))}
                            placeholder={`e.g., Redesign career page to highlight Kubernetes and hybrid work`}
                            required
                            rows={2}
                            style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label htmlFor={`owner${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Owner <span style={{ color: '#dc2626' }}>*</span></label>
                            <input 
                              type="text" 
                              id={`owner${num}`}
                              name={`owner${num}`}
                              value={actionPlanForm[`owner${num}` as keyof typeof actionPlanForm]}
                              onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`owner${num}`]: e.target.value }))}
                              placeholder="e.g., TA Lead"
                              required
                              style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label htmlFor={`deadline${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Deadline <span style={{ color: '#dc2626' }}>*</span></label>
                            <input 
                              type="text" 
                              id={`deadline${num}`}
                              name={`deadline${num}`}
                              value={actionPlanForm[`deadline${num}` as keyof typeof actionPlanForm]}
                              onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`deadline${num}`]: e.target.value }))}
                              placeholder="e.g., Week 1"
                              required
                              style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label htmlFor={`outcome${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Expected Outcome <span style={{ color: '#dc2626' }}>*</span></label>
                          <textarea 
                            id={`outcome${num}`}
                            name={`outcome${num}`}
                            value={actionPlanForm[`outcome${num}` as keyof typeof actionPlanForm]}
                            onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`outcome${num}`]: e.target.value }))}
                            placeholder="e.g., 20% more senior applicants"
                            required
                            rows={2}
                            style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                          />
                        </div>
                      </div>
                    ))}
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingActionPlan}
                      style={{
                        background: isSubmittingActionPlan ? '#ccc' : 'var(--orange)',
                        color: 'white',
                        border: 'none',
                        padding: '1rem 2rem',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: isSubmittingActionPlan ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        alignSelf: 'flex-start'
                      }}
                      onMouseOver={(e) => {
                        if (!isSubmittingActionPlan) {
                          e.currentTarget.style.background = '#d4a017';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isSubmittingActionPlan) {
                          e.currentTarget.style.background = 'var(--orange)';
                        }
                      }}
                    >
                      {isSubmittingActionPlan ? 'Submitting...' : 'Submit Action Plan'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 8 Hands-On Activity section (30-Day Action Plan)
        if (moduleId === 8 && section.id === 5) {
          const handleActionPlanActivitySubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingActionPlan(true);

            try {
              const submission = {
                role: actionPlanForm.role,
                actions: [
                  {
                    action: actionPlanForm.action1,
                    owner: actionPlanForm.owner1,
                    deadline: actionPlanForm.deadline1,
                    outcome: actionPlanForm.outcome1
                  },
                  {
                    action: actionPlanForm.action2,
                    owner: actionPlanForm.owner2,
                    deadline: actionPlanForm.deadline2,
                    outcome: actionPlanForm.outcome2
                  },
                  {
                    action: actionPlanForm.action3,
                    owner: actionPlanForm.owner3,
                    deadline: actionPlanForm.deadline3,
                    outcome: actionPlanForm.outcome3
                  }
                ],
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 5
              };

              const response = await fetch(apiUrl('/api/module-submissions/action-plan-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowActionPlanSuccessModal(true);
                setActionPlanForm({
                  role: '',
                  action1: '', owner1: '', deadline1: '', outcome1: '',
                  action2: '', owner2: '', deadline2: '', outcome2: '',
                  action3: '', owner3: '', deadline3: '', outcome3: ''
                });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting action plan:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingActionPlan(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>{section.title}</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Create a 30-day action plan for hiring a principal IT role (e.g., Senior AI Engineer) in CZ or US. List 3 actions with owners, deadlines, and expected outcomes. Submit below. We'll review and offer tailored feedback (optional).</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Sample Action Plan</h3>
                      <p><strong>Role:</strong> Senior DevOps Engineer (Travel Tech Company, CZ)</p>
                      <div style={{ marginTop: '1rem' }}>
                        <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Actions:</p>
                        <ol style={{ marginLeft: '1.5rem', paddingLeft: '0', lineHeight: '1.8' }}>
                          <li><strong>Action:</strong> Redesign career page to highlight Kubernetes and hybrid work. <strong>Owner:</strong> TA Lead. <strong>Deadline:</strong> Week 1. <strong>Outcome:</strong> 20% more senior applicants.</li>
                          <li><strong>Action:</strong> Join Prague Devs meetup to source candidates. <strong>Owner:</strong> Recruiter. <strong>Deadline:</strong> Week 2. <strong>Outcome:</strong> 5 senior contacts added to pipeline.</li>
                          <li><strong>Action:</strong> Test GitHub outreach for Kubernetes experts. <strong>Owner:</strong> TA Manager. <strong>Deadline:</strong> Week 3. <strong>Outcome:</strong> 2 senior candidates shortlisted.</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Time & Purpose</h3>
                      <p><strong>Time:</strong> 10 minutes (self-paced).</p>
                      <p><strong>Purpose:</strong> Commits TA teams to actionable steps, addressing CZ's undertrained teams and US's competitive hiring.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-form" style={{ marginTop: '3rem', padding: '2rem', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e9ecef' }}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Submit Your 30-Day Action Plan</h3>
                  <form onSubmit={handleActionPlanActivitySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="role-action-activity" style={{ fontWeight: '600', color: 'var(--dark)' }}>Role <span style={{ color: '#dc2626' }}>*</span></label>
                      <input 
                        type="text" 
                        id="role-action-activity" 
                        name="role" 
                        value={actionPlanForm.role}
                        onChange={(e) => setActionPlanForm(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Senior DevOps Engineer (Travel Tech Company, CZ)"
                        required
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                      />
                    </div>
                    
                    {[1, 2, 3].map((num) => (
                      <div key={num} style={{ 
                        padding: '1.5rem', 
                        background: 'white', 
                        borderRadius: '8px', 
                        border: '1px solid #e9ecef',
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '1rem' 
                      }}>
                        <h4 style={{ margin: 0, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Action {num}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label htmlFor={`action-activity-${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Action <span style={{ color: '#dc2626' }}>*</span></label>
                          <textarea 
                            id={`action-activity-${num}`}
                            name={`action${num}`}
                            value={actionPlanForm[`action${num}` as keyof typeof actionPlanForm]}
                            onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`action${num}`]: e.target.value }))}
                            placeholder={`e.g., Redesign career page to highlight Kubernetes and hybrid work`}
                            required
                            rows={2}
                            style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label htmlFor={`owner-activity-${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Owner <span style={{ color: '#dc2626' }}>*</span></label>
                            <input 
                              type="text" 
                              id={`owner-activity-${num}`}
                              name={`owner${num}`}
                              value={actionPlanForm[`owner${num}` as keyof typeof actionPlanForm]}
                              onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`owner${num}`]: e.target.value }))}
                              placeholder="e.g., TA Lead"
                              required
                              style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label htmlFor={`deadline-activity-${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Deadline <span style={{ color: '#dc2626' }}>*</span></label>
                            <input 
                              type="text" 
                              id={`deadline-activity-${num}`}
                              name={`deadline${num}`}
                              value={actionPlanForm[`deadline${num}` as keyof typeof actionPlanForm]}
                              onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`deadline${num}`]: e.target.value }))}
                              placeholder="e.g., Week 1"
                              required
                              style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label htmlFor={`outcome-activity-${num}`} style={{ fontWeight: '600', color: 'var(--dark)' }}>Expected Outcome <span style={{ color: '#dc2626' }}>*</span></label>
                          <textarea 
                            id={`outcome-activity-${num}`}
                            name={`outcome${num}`}
                            value={actionPlanForm[`outcome${num}` as keyof typeof actionPlanForm]}
                            onChange={(e) => setActionPlanForm(prev => ({ ...prev, [`outcome${num}`]: e.target.value }))}
                            placeholder="e.g., 20% more senior applicants"
                            required
                            rows={2}
                            style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                          />
                        </div>
                      </div>
                    ))}
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingActionPlan}
                      style={{
                        background: isSubmittingActionPlan ? '#ccc' : 'var(--orange)',
                        color: 'white',
                        border: 'none',
                        padding: '1rem 2rem',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: isSubmittingActionPlan ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        alignSelf: 'flex-start'
                      }}
                      onMouseOver={(e) => {
                        if (!isSubmittingActionPlan) {
                          e.currentTarget.style.background = '#d4a017';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isSubmittingActionPlan) {
                          e.currentTarget.style.background = 'var(--orange)';
                        }
                      }}
                    >
                      {isSubmittingActionPlan ? 'Submitting...' : 'Submit Action Plan'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 7 Activity section (Dataset Analysis)
        if (moduleId === 7 && section.id === 4) {
          const handleDatasetAnalysisSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingDataset(true);

            try {
              const submission = {
                role: datasetAnalysisForm.role,
                improvements: datasetAnalysisForm.improvements,
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 4
              };

              const response = await fetch(apiUrl('/api/module-submissions/dataset-analysis-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowDatasetAnalysisSuccessModal(true);
                setDatasetAnalysisForm({ role: '', improvements: '' });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting dataset analysis:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingDataset(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>{section.title}</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Review a sample hiring dataset for a principal IT role (e.g., Senior Cloud Architect) in CZ or US. Dataset: 50 applicants, 10% senior-qualified, 90-day time-to-hire, 60% acceptance rate. Suggest 2 improvements to boost effectiveness. Submit below. We'll review and offer tailored feedback (optional).</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Sample Dataset</h3>
                      <p><strong>Role:</strong> Senior Cloud Architect (Kiwi.com, CZ)</p>
                      <p><strong>Metrics:</strong> 50 applicants, 5 senior-qualified (10%), 90-day time-to-hire, 60% acceptance rate.</p>
                      <p style={{ marginTop: '1rem', fontWeight: '600' }}>Improvements:</p>
                      <ol style={{ marginLeft: '1.5rem', paddingLeft: '0' }}>
                        <li>Shift sourcing from Jobs.cz to GitHub for Kubernetes experts.</li>
                        <li>Offer hybrid work and €1K learning budget to boost acceptance to 80%.</li>
                      </ol>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Time & Purpose</h3>
                      <p><strong>Time:</strong> 15 minutes (self-paced).</p>
                      <p><strong>Purpose:</strong> Builds data-driven skills for undertrained CZ TA teams and US teams facing Big Tech competition.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-form" style={{ marginTop: '3rem', padding: '2rem', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e9ecef' }}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Submit Your Dataset Analysis</h3>
                  <form onSubmit={handleDatasetAnalysisSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="role-dataset" style={{ fontWeight: '600', color: 'var(--dark)' }}>Role <span style={{ color: '#dc2626' }}>*</span></label>
                      <input 
                        type="text" 
                        id="role-dataset" 
                        name="role" 
                        value={datasetAnalysisForm.role}
                        onChange={(e) => setDatasetAnalysisForm(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Senior Cloud Architect (Travel Tech Company, CZ)"
                        required
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="improvements" style={{ fontWeight: '600', color: 'var(--dark)' }}>Suggested Improvements (List 2) <span style={{ color: '#dc2626' }}>*</span></label>
                      <textarea 
                        id="improvements" 
                        name="improvements" 
                        value={datasetAnalysisForm.improvements}
                        onChange={(e) => setDatasetAnalysisForm(prev => ({ ...prev, improvements: e.target.value }))}
                        placeholder="1. Shift sourcing from Jobs.cz to GitHub for Kubernetes experts.&#10;2. Offer hybrid work and €1K learning budget to boost acceptance to 80%."
                        required
                        rows={6}
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingDataset}
                      style={{
                        background: isSubmittingDataset ? '#ccc' : 'var(--orange)',
                        color: 'white',
                        border: 'none',
                        padding: '1rem 2rem',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: isSubmittingDataset ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        alignSelf: 'flex-start'
                      }}
                      onMouseOver={(e) => {
                        if (!isSubmittingDataset) {
                          e.currentTarget.style.background = '#d4a017';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isSubmittingDataset) {
                          e.currentTarget.style.background = 'var(--orange)';
                        }
                      }}
                    >
                      {isSubmittingDataset ? 'Submitting...' : 'Submit Analysis'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 7 Hands-On Activity section (Challenge Identification + Action Plan)
        if (moduleId === 7 && section.id === 5) {
          const handleHandsOnActivitySubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingHandsOnActivity(true);

            try {
              const submission = {
                role: handsOnActivityForm.role,
                challenge1: handsOnActivityForm.challenge1,
                challenge2: handsOnActivityForm.challenge2,
                challenge3: handsOnActivityForm.challenge3,
                metrics: handsOnActivityForm.metrics,
                targets: handsOnActivityForm.targets,
                actions: [
                  {
                    action: handsOnActivityForm.action1,
                    owner: handsOnActivityForm.owner1,
                    deadline: handsOnActivityForm.deadline1,
                    outcome: handsOnActivityForm.outcome1
                  },
                  {
                    action: handsOnActivityForm.action2,
                    owner: handsOnActivityForm.owner2,
                    deadline: handsOnActivityForm.deadline2,
                    outcome: handsOnActivityForm.outcome2
                  },
                  {
                    action: handsOnActivityForm.action3,
                    owner: handsOnActivityForm.owner3,
                    deadline: handsOnActivityForm.deadline3,
                    outcome: handsOnActivityForm.outcome3
                  }
                ],
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 5
              };

              const response = await fetch(apiUrl('/api/module-submissions/action-plan-hands-on-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowHandsOnActivitySuccessModal(true);
                setHandsOnActivityForm({
                  role: '',
                  challenge1: '',
                  challenge2: '',
                  challenge3: '',
                  metrics: '',
                  targets: '',
                  action1: '',
                  owner1: '',
                  deadline1: '',
                  outcome1: '',
                  action2: '',
                  owner2: '',
                  deadline2: '',
                  outcome2: '',
                  action3: '',
                  owner3: '',
                  deadline3: '',
                  outcome3: ''
                });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting hands-on activity:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingHandsOnActivity(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>Hands-On Activity (you need the PDF)</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Open the kit → complete the 30-min Prep (fill Challenge Form for your biggest issue) + 45-min Framework (build 3-action plan) → submit async for optional feedback.</p>
                      <p><strong>Earn the "Transformation Closer" badge + course completion certificate.</strong></p>
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <p style={{ marginBottom: '0.75rem', fontWeight: '600', color: 'var(--dark)' }}>Before You Start:</p>
                        <p style={{ marginBottom: '0.5rem', color: 'var(--gray)' }}>Make sure you've downloaded the <strong>Action Plan Hacker Kit (30-Day Template Basics)</strong> from the "Before You Start" section.</p>
                        <p style={{ color: 'var(--gray)', margin: 0 }}>Review the "Prep for Planning" and "Action Plan Framework" sections in the PDF to guide your work.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>What You'll Complete</h3>
                      <p><strong>Part 1 - Challenge Identification Form (30 min):</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0', marginBottom: '1rem' }}>
                        <li>Define a principal IT role you're hiring for</li>
                        <li>List top 3 challenges/hurdles per role</li>
                        <li>Identify metrics and targets</li>
                      </ul>
                      <p><strong>Part 2 - Action Plan Framework (45 min):</strong></p>
                      <ul style={{ marginLeft: '1.5rem', paddingLeft: '0' }}>
                        <li>Build a 3-action plan with owners, deadlines, and expected outcomes</li>
                        <li>Each action should address the challenges identified</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Submission & Purpose</h3>
                      <p><strong>Time:</strong> 75 minutes total (30 min Prep + 45 min Framework, self-paced)</p>
                      <p><strong>Purpose:</strong> Commits TA teams to actionable steps, addressing CZ's undertrained teams and US's competitive hiring.</p>
                      <p style={{ marginTop: '0.75rem', color: 'var(--gray)', fontStyle: 'italic' }}>Your input will be sent to us for optional personalised feedback. We'll review your challenge identification and action plan to help you refine your hiring strategy.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-note" style={{
                  marginTop: '2rem',
                  padding: '1rem',
                  background: 'rgba(216, 183, 37, 0.1)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: 'var(--dark)',
                  fontSize: '0.9rem'
                }}>
                  <Lock size={18} style={{ color: 'var(--orange)' }} />
                  <span>Submit your completed activity below. I'll review it as admin and offer tailored feedback (optional) to help you transform your hiring effectively.</span>
                </div>
                
                <div className="activity-form">
                  <h3>Submit Your Hands-On Activity</h3>
                  <form onSubmit={handleHandsOnActivitySubmit}>
                    <div className="form-section-white">
                      <h4>Part 1: Challenge Identification Form (30-min Prep)</h4>
                      
                      <div className="form-field-group">
                        <label htmlFor="hands-on-role" className="form-label">Principal IT Role <span className="form-label-required">*</span></label>
                        <input 
                          type="text" 
                          id="hands-on-role" 
                          name="role" 
                          value={handsOnActivityForm.role}
                          onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, role: e.target.value }))}
                          placeholder="e.g., Senior AI Engineer, Principal DevOps Engineer"
                          required
                          className="form-input"
                        />
                      </div>
                      
                      <div className="form-field-group">
                        <label htmlFor="challenge1" className="form-label">Challenge 1 <span className="form-label-required">*</span></label>
                        <textarea 
                          id="challenge1"
                          name="challenge1"
                          value={handsOnActivityForm.challenge1}
                          onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, challenge1: e.target.value }))}
                          placeholder="e.g., Low response rate from passive candidates"
                          required
                          rows={2}
                          className="form-textarea"
                        />
                      </div>
                      
                      <div className="form-field-group">
                        <label htmlFor="challenge2" className="form-label">Challenge 2 <span className="form-label-required">*</span></label>
                        <textarea 
                          id="challenge2"
                          name="challenge2"
                          value={handsOnActivityForm.challenge2}
                          onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, challenge2: e.target.value }))}
                          placeholder="e.g., Long time-to-hire (90+ days)"
                          required
                          rows={2}
                          className="form-textarea"
                        />
                      </div>
                      
                      <div className="form-field-group">
                        <label htmlFor="challenge3" className="form-label">Challenge 3 <span className="form-label-required">*</span></label>
                        <textarea 
                          id="challenge3"
                          name="challenge3"
                          value={handsOnActivityForm.challenge3}
                          onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, challenge3: e.target.value }))}
                          placeholder="e.g., Low acceptance rate from senior candidates"
                          required
                          rows={2}
                          className="form-textarea"
                        />
                      </div>
                      
                      <div className="form-field-group">
                        <label htmlFor="metrics" className="form-label">Metrics (Optional)</label>
                        <textarea 
                          id="metrics"
                          name="metrics"
                          value={handsOnActivityForm.metrics}
                          onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, metrics: e.target.value }))}
                          placeholder="e.g., Time-to-hire, acceptance rate, response rate"
                          rows={2}
                          className="form-textarea"
                        />
                      </div>
                      
                      <div className="form-field-group">
                        <label htmlFor="targets" className="form-label">Targets (Optional)</label>
                        <textarea 
                          id="targets"
                          name="targets"
                          value={handsOnActivityForm.targets}
                          onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, targets: e.target.value }))}
                          placeholder="e.g., Reduce time-to-hire to 45 days, increase acceptance rate to 80%"
                          rows={2}
                          className="form-textarea"
                        />
                      </div>
                    </div>
                    
                    <div className="form-section-white">
                      <h4>Part 2: Action Plan Framework (45-min Framework)</h4>
                      
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="form-section-gray">
                          <h4>Action {num}</h4>
                          <div className="form-field-group">
                            <label htmlFor={`hands-on-action${num}`} className="form-label">Action <span className="form-label-required">*</span></label>
                            <textarea 
                              id={`hands-on-action${num}`}
                              name={`action${num}`}
                              value={handsOnActivityForm[`action${num}` as keyof typeof handsOnActivityForm]}
                              onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, [`action${num}`]: e.target.value }))}
                              placeholder={`e.g., Redesign career page to highlight Kubernetes and hybrid work`}
                              required
                              rows={2}
                              className="form-textarea"
                            />
                          </div>
                          <div className="form-grid">
                            <div className="form-field-group">
                              <label htmlFor={`hands-on-owner${num}`} className="form-label">Owner <span className="form-label-required">*</span></label>
                              <input 
                                type="text" 
                                id={`hands-on-owner${num}`}
                                name={`owner${num}`}
                                value={handsOnActivityForm[`owner${num}` as keyof typeof handsOnActivityForm]}
                                onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, [`owner${num}`]: e.target.value }))}
                                placeholder="e.g., TA Lead"
                                required
                                className="form-input"
                              />
                            </div>
                            <div className="form-field-group">
                              <label htmlFor={`hands-on-deadline${num}`} className="form-label">Deadline <span className="form-label-required">*</span></label>
                              <input 
                                type="text" 
                                id={`hands-on-deadline${num}`}
                                name={`deadline${num}`}
                                value={handsOnActivityForm[`deadline${num}` as keyof typeof handsOnActivityForm]}
                                onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, [`deadline${num}`]: e.target.value }))}
                                placeholder="e.g., Week 1"
                                required
                                className="form-input"
                              />
                            </div>
                          </div>
                          <div className="form-field-group">
                            <label htmlFor={`hands-on-outcome${num}`} className="form-label">Expected Outcome <span className="form-label-required">*</span></label>
                            <textarea 
                              id={`hands-on-outcome${num}`}
                              name={`outcome${num}`}
                              value={handsOnActivityForm[`outcome${num}` as keyof typeof handsOnActivityForm]}
                              onChange={(e) => setHandsOnActivityForm(prev => ({ ...prev, [`outcome${num}`]: e.target.value }))}
                              placeholder="e.g., 20% more senior applicants"
                              required
                              rows={2}
                              className="form-textarea"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingHandsOnActivity}
                      className="form-submit-btn"
                    >
                      {isSubmittingHandsOnActivity ? 'Submitting...' : 'Submit Hands-On Activity'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Special handling for Module 6 Activity section (Pipeline Plan)
        if (moduleId === 6 && section.id === 5) {
          const handlePipelinePlanSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmittingPipeline(true);

            try {
              const submission = {
                role: pipelinePlanForm.role,
                sourcingChannels: pipelinePlanForm.sourcingChannels,
                nurturingTactics: pipelinePlanForm.nurturingTactics,
                submittedAt: new Date().toISOString(),
                moduleId: moduleId,
                sectionId: 5
              };

              const response = await fetch(apiUrl('/api/module-submissions/pipeline-plan-submission'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                },
                body: JSON.stringify(submission)
              });

              const result = await response.json();
              
              if (result.success) {
                setShowPipelinePlanSuccessModal(true);
                setPipelinePlanForm({ role: '', sourcingChannels: '', nurturingTactics: '' });
              } else {
                alert('Failed to submit. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting pipeline plan:', error);
              alert('Something went wrong. Please try again.');
            } finally {
              setIsSubmittingPipeline(false);
            }
          };

          return (
            <div className="module-section">
              <div className="section-header title-with-icon">
                <IconComponent className="section-icon" />
                <h2>{section.title}</h2>
              </div>
              
              <div className="content-body">
                <div className="challenge-cards" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <FileText size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Instructions</h3>
                      <p>Create a pipeline plan for a principal IT role (e.g., Senior AI Engineer) in CZ or US. Complete two tasks:</p>
                      <ol style={{ marginLeft: '1.5rem', paddingLeft: '0', marginTop: '1rem' }}>
                        <li style={{ marginBottom: '0.5rem' }}>List 3 sourcing channels (e.g., GitHub, Prague Devs, IEEE).</li>
                        <li style={{ marginBottom: '0.5rem' }}>List 2 nurturing tactics (e.g., monthly emails, meetup invites).</li>
                      </ol>
                      <p style={{ marginTop: '1rem' }}>Submit below or download the template. We'll review and offer tailored feedback (optional) to optimise your pipeline.</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Sample Submission</h3>
                      <p><strong>Role:</strong> Senior AI Engineer (Productboard, CZ)</p>
                      <p><strong>Sourcing Channels:</strong> GitHub (Python repos), Prague Devs meetup, LinkedIn "Prague Tech Expat Network."</p>
                      <p><strong>Nurturing Tactics:</strong> Monthly emails with CzechCrunch updates, invites to Prague Tech Fest.</p>
                    </div>
                  </div>
                  
                  <div className="challenge-card">
                    <div className="challenge-icon">
                      <Clock size={24} />
                    </div>
                    <div className="challenge-content">
                      <h3>Time & Purpose</h3>
                      <p><strong>Time:</strong> 15 minutes (self-paced).</p>
                      <p><strong>Purpose:</strong> Helps TA teams plan proactive pipelines, addressing CZ's undertrained teams and US's competitive hiring.</p>
                    </div>
                  </div>
                </div>
                
                <div className="activity-form">
                  <h3>Submit Your Pipeline Plan</h3>
                  <form onSubmit={handlePipelinePlanSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="role-pipeline" style={{ fontWeight: '600', color: 'var(--dark)' }}>Role <span style={{ color: '#dc2626' }}>*</span></label>
                      <input 
                        type="text" 
                        id="role-pipeline" 
                        name="role" 
                        value={pipelinePlanForm.role}
                        onChange={(e) => setPipelinePlanForm(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Senior AI Engineer (Productboard, CZ)"
                        required
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)' }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="sourcing-channels" style={{ fontWeight: '600', color: 'var(--dark)' }}>Sourcing Channels (List 3) <span style={{ color: '#dc2626' }}>*</span></label>
                      <textarea 
                        id="sourcing-channels" 
                        name="sourcingChannels" 
                        value={pipelinePlanForm.sourcingChannels}
                        onChange={(e) => setPipelinePlanForm(prev => ({ ...prev, sourcingChannels: e.target.value }))}
                        placeholder="e.g., GitHub (Python repos), Prague Devs meetup, LinkedIn 'Prague Tech Expat Network'"
                        required
                        rows={4}
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label htmlFor="nurturing-tactics" style={{ fontWeight: '600', color: 'var(--dark)' }}>Nurturing Tactics (List 2) <span style={{ color: '#dc2626' }}>*</span></label>
                      <textarea 
                        id="nurturing-tactics" 
                        name="nurturingTactics" 
                        value={pipelinePlanForm.nurturingTactics}
                        onChange={(e) => setPipelinePlanForm(prev => ({ ...prev, nurturingTactics: e.target.value }))}
                        placeholder="e.g., Monthly emails with CzechCrunch updates, invites to Prague Tech Fest"
                        required
                        rows={4}
                        style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'var(--montserrat)', resize: 'vertical' }}
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isSubmittingPipeline}
                      style={{
                        background: isSubmittingPipeline ? '#ccc' : 'var(--orange)',
                        color: 'white',
                        border: 'none',
                        padding: '1rem 2rem',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: isSubmittingPipeline ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        alignSelf: 'flex-start'
                      }}
                      onMouseOver={(e) => {
                        if (!isSubmittingPipeline) {
                          e.currentTarget.style.background = '#d4a017';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isSubmittingPipeline) {
                          e.currentTarget.style.background = 'var(--orange)';
                        }
                      }}
                    >
                      {isSubmittingPipeline ? 'Submitting...' : 'Submit Pipeline Plan'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          );
        }
        
        // Default activity section for other modules
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <IconComponent className="section-icon" />
              <h2>{section.title}</h2>
            </div>
            
            <div className="content-body">
              <p className="lead-text">
                Practice sourcing a principal IT role (e.g., Principal AI Engineer, Senior Cloud Architect) for your company in CZ or US. Complete two tasks:
              </p>
              
              <div className="activity-container" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2rem' }}>
                {/* Task 1 Card */}
                <div className="challenge-card" style={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  borderLeft: '4px solid var(--orange)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}>
                  <h4 style={{ 
                    fontSize: '1.2rem', 
                    fontWeight: '700', 
                    color: 'var(--dark)', 
                    margin: '0 0 1rem 0',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Task 1: Build a Boolean Search
                  </h4>
                  <p style={{ 
                    color: '#6c757d', 
                    lineHeight: '1.7', 
                    margin: 0,
                    fontFamily: 'var(--raleway)',
                    fontSize: '1rem'
                  }}>
                    Create a LinkedIn Recruiter search string (e.g., "senior AND (developer OR engineer) AND AI AND Prague -junior" for CZ).
                  </p>
                </div>
                
                {/* Task 2 Card */}
                <div className="challenge-card" style={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  borderLeft: '4px solid var(--orange)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}>
                  <h4 style={{ 
                    fontSize: '1.2rem', 
                    fontWeight: '700', 
                    color: 'var(--dark)', 
                    margin: '0 0 1rem 0',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Task 2: Draft a Cold Outreach Email
                  </h4>
                  <p style={{ 
                    color: '#6c757d', 
                    lineHeight: '1.7', 
                    margin: 0,
                    fontFamily: 'var(--raleway)',
                    fontSize: '1rem'
                  }}>
                    Write an email to a GitHub contributor (e.g., "Hi [Name], your [repo] work is impressive…"). Keep it under 100 words, focus on project impact.
                  </p>
                </div>
                
                {/* Sample Submission Card */}
                <div className="challenge-card" style={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  borderLeft: '4px solid var(--orange)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}>
                  <h4 style={{ 
                    fontSize: '1.2rem', 
                    fontWeight: '700', 
                    color: 'var(--dark)', 
                    margin: '0 0 1rem 0',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Sample Submission
                  </h4>
                  <div style={{ 
                    color: '#6c757d', 
                    lineHeight: '1.7',
                    fontFamily: 'var(--raleway)',
                    fontSize: '1rem'
                  }}>
                    <p style={{ margin: '0.5rem 0' }}>
                      <strong style={{ color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Role:</strong> Principal Cloud Architect (Data Analytics Company, US)
                    </p>
                    <p style={{ margin: '0.5rem 0' }}>
                      <strong style={{ color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Boolean Search:</strong> "senior AND (architect OR engineer) AND (AWS OR Azure) AND (San Francisco OR remote) -junior"
                    </p>
                    <p style={{ margin: '0.5rem 0' }}>
                      <strong style={{ color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>Email:</strong> "Hi Jane, your Kubernetes repo contributions caught my eye. We're building a cloud platform at our data analytics company and need a principal architect to lead migrations. Interested in a remote role with 0.2% equity? Let's chat. Regards, [Your Name]."
                    </p>
                  </div>
                </div>
                
                <div className="activity-note" style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0.5rem 0', fontFamily: 'var(--raleway)', color: 'var(--gray)' }}>
                    <strong style={{ fontFamily: 'var(--montserrat)' }}>Time:</strong> 10 minutes (self-paced)
                  </p>
                  <p style={{ margin: '0.5rem 0', fontFamily: 'var(--raleway)', color: 'var(--gray)' }}>
                    <strong style={{ fontFamily: 'var(--montserrat)' }}>Purpose:</strong> Builds practical skills for undertrained CZ TA teams and US teams facing Big Tech competition.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'takeaway':
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <IconComponent className="section-icon" />
              <h2>{section.title}</h2>
            </div>
            
            <div className="content-body">
              <div className="resource-card">
                <div className="resource-header">
                  <div className="resource-icon">
                    <FileText />
                  </div>
                  <div className="resource-info">
                    <h3>
                      {moduleId === 1 ? 'Market Insights Hacker Kit' :
                       moduleId === 3 ? 'Sourcing Cheat Sheet for Principal IT Talent' : 
                       moduleId === 4 ? 'Senior-Friendly Employer Branding Checklist' : 
                       moduleId === 5 ? 'Outreach and Screening Toolkit for Principal IT Talent' :
                       moduleId === 6 ? 'Senior IT Talent Pipeline Template' :
                       moduleId === 7 ? 'Recruitment Metrics Dashboard for Senior IT Talent' :
                       moduleId === 8 ? '30-Day TA Action Plan for Senior IT Hiring' :
                       'Principal IT Role Job Description Template'}
                    </h3>
                    <p>
                      {moduleId === 1 ? 'The Market Insights Hacker Kit (two checklists: Czech + US 2025) is the exact data pack we use when sourcing for our clients. Without it → outdated assumptions. With it → you speak the market like a pro and spot opportunities others miss.' :
                       moduleId === 3 ? 'Downloadable PDF cheat sheet with Boolean search strings, tech community links, and GitHub outreach templates for sourcing principal IT talent in CZ and US markets.' :
                       moduleId === 4 ? 'Downloadable PDF checklist (15 pages) with all items needed to enhance your employer brand for attracting principal IT talent in CZ and US markets.' :
                       moduleId === 5 ? 'Downloadable PDF toolkit with outreach email samples, screening rubric, and quick tips for screening and engaging principal IT talent in CZ and US markets.' :
                       moduleId === 6 ? 'Downloadable PDF template with columns for Name, Skills, Contact, Last Outreach, and Status. Includes CZ/US-specific tips and example entries for building a 20–50 candidate pipeline.' :
                       moduleId === 7 ? 'Downloadable PDF dashboard template with metrics (Applicants, Shortlisted, Time-to-Hire, Offer Acceptance Rate, Candidate Quality). Includes CZ/US-specific tips and example entries for tracking and optimising recruitment success.' :
                       moduleId === 8 ? 'Downloadable PDF template with structure for Action, Owner, Deadline, and Expected Outcome. Includes CZ/US-specific tips and example entries for creating a 30-day action plan to transform hiring.' :
                       'Downloadable PDF template with all sections needed for creating effective job descriptions for principal IT roles in CZ and US markets.'}
                    </p>
                  </div>
                </div>
                {moduleId === 1 ? (
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <button 
                      className="download-btn"
                      onClick={() => handleResourceDownload('czech-it-talent-market-2025-checklist.pdf')}
                      disabled={isDownloading}
                      style={{ opacity: isDownloading ? 0.6 : 1, cursor: isDownloading ? 'not-allowed' : 'pointer', flex: '1', minWidth: '200px' }}
                    >
                      <Download />
                      {isDownloading ? 'Downloading...' : 'Download Czech PDF'}
                    </button>
                    <button 
                      className="download-btn"
                      onClick={() => handleResourceDownload('us-it-talent-market-2025-checklist.pdf')}
                      disabled={isDownloading}
                      style={{ opacity: isDownloading ? 0.6 : 1, cursor: isDownloading ? 'not-allowed' : 'pointer', flex: '1', minWidth: '200px' }}
                    >
                      <Download />
                      {isDownloading ? 'Downloading...' : 'Download US PDF'}
                    </button>
                  </div>
                ) : (
                  <button 
                    className="download-btn"
                    onClick={() => handleResourceDownload(
                      moduleId === 3 ? 'sourcing-cheat-sheet.pdf' :
                      moduleId === 4 ? 'employer-branding-checklist.pdf' :
                      moduleId === 5 ? 'screening-engagement-toolkit.pdf' :
                      moduleId === 6 ? 'talent-pipeline-template.pdf' :
                      moduleId === 7 ? 'recruitment-metrics-dashboard.pdf' :
                      moduleId === 8 ? '30-day-action-plan-template.pdf' :
                      'principal-job-description-template.pdf'
                    )}
                    disabled={isDownloading}
                    style={{ opacity: isDownloading ? 0.6 : 1, cursor: isDownloading ? 'not-allowed' : 'pointer' }}
                  >
                    <Download />
                    {isDownloading ? 'Downloading...' : 'Download PDF'}
                  </button>
                )}
              </div>

              <div className="checklist-preview">
                <h4>What's Inside:</h4>
                <ul>
                  {moduleId === 1 ? (
                    <>
                      <li><CheckCircle /><strong>Key Stats:</strong> CZ ~15,000 new IT jobs 2025, 63% shortage, €60K–€100K principals; US 317,700 openings, 74% shortage, $150K–$250K total comp</li>
                      <li><CheckCircle /><strong>Principal Identification Checklist:</strong> 10+ years niche expertise, leadership proof, motivators (impact/autonomy CZ, equity/growth US)</li>
                      <li><CheckCircle /><strong>Creative Sourcing Strategies:</strong> GitHub repos, niche forums (e.g., cybersecurity specialist hires via universities/forums – none on LinkedIn), referrals (€2K/$5K bonuses), AI tools (80% screening time saved)</li>
                      <li><CheckCircle /><strong>Salary/Negotiation Tips:</strong> CZ counter US offers with hybrid + learning budgets; US emphasize equity + fast offers</li>
                      <li><CheckCircle /><strong>Regional Comparisons:</strong> CZ tight 2.7% unemployment vs. US 4.3%, hybrid prefs, undertrained TA teams</li>
                      <li><CheckCircle /><strong>Case Insights:</strong> Cybersecurity firms sourcing specialists via non-LinkedIn hacks; high-tech companies finding C++ developers via developer forums</li>
                    </>
                  ) : moduleId === 3 ? (
                    <>
                      <li><CheckCircle /><strong>10 Boolean Search Strings:</strong> Ready-to-use searches for AI, cybersecurity, devops roles in CZ and US</li>
                      <li><CheckCircle /><strong>Tech Community Links:</strong> 5 CZ links (Prague Devs, Czech ICT Summit) and 5 US links (TechCrunch Disrupt, IEEE)</li>
                      <li><CheckCircle /><strong>GitHub Outreach Email Template:</strong> Template for engaging passive contributors</li>
                      <li><CheckCircle /><strong>Quick Tips:</strong> Market-specific guidance for CZ (work-life balance) and US (equity, high-impact projects)</li>
                    </>
                  ) : moduleId === 4 ? (
                    <>
                      <li><CheckCircle />Tech Stack: List core technologies (e.g., React, AWS, TensorFlow)</li>
                      <li><CheckCircle />Project Impact: Highlight high-stakes projects</li>
                      <li><CheckCircle />Culture: Emphasize autonomy, hybrid/remote options (70% CZ preference, 30% US hybrid)</li>
                      <li><CheckCircle />Testimonials: Include senior engineer stories</li>
                      <li><CheckCircle />Platforms: Update Jobs.cz (CZ) or Glassdoor (US) with salary ranges</li>
                      <li><CheckCircle />Content: Publish tech blogs or contribute to GitHub repos</li>
                      <li><CheckCircle />Events: Speak at Prague Tech Fest (CZ) or TechCrunch Disrupt (US)</li>
                    </>
                  ) : moduleId === 5 ? (
                    <>
                      <li><CheckCircle /><strong>3 Outreach Email Samples:</strong> LinkedIn (CZ), GitHub (US), and Referral (CZ/US) templates</li>
                      <li><CheckCircle /><strong>Screening Rubric:</strong> Skills (40%), Experience (30%), Leadership/Culture (30%)</li>
                      <li><CheckCircle /><strong>Quick Tips:</strong> CZ (hybrid work, work-life balance) and US (equity, enterprise projects) guidance</li>
                      <li><CheckCircle /><strong>Assessment Tools:</strong> Codility (CZ) and HackerRank (US) recommendations</li>
                      <li><CheckCircle /><strong>Interview Flexibility:</strong> Best practices for accommodating senior schedules</li>
                    </>
                  ) : moduleId === 6 ? (
                    <>
                      <li><CheckCircle /><strong>Pipeline Columns:</strong> Name, Skills (e.g., Python, Kubernetes), Contact (LinkedIn, email), Last Outreach (date), Status (Interested, Not Interested, In Talks)</li>
                      <li><CheckCircle /><strong>CZ Tips:</strong> Source from Prague/Brno (Prague Devs), nurture with CzechCrunch updates</li>
                      <li><CheckCircle /><strong>US Tips:</strong> Target tech hubs/remote via IEEE, offer equity (0.2–0.5%) in outreach</li>
                      <li><CheckCircle /><strong>Best Practices:</strong> Update pipeline monthly; aim for 20–50 candidates total; log all interactions</li>
                      <li><CheckCircle /><strong>Example Entries:</strong> Sample pipeline for 10 senior AI engineers in Prague with complete data</li>
                    </>
                  ) : moduleId === 7 ? (
                    <>
                      <li><CheckCircle /><strong>Key Metrics:</strong> Applicants, Shortlisted (e.g., 5 senior-qualified, 10%), Time-to-Hire (target {'<60'} days), Offer Acceptance Rate (target {'>80'}%), Candidate Quality (target 90% pass technical interviews)</li>
                      <li><CheckCircle /><strong>CZ Tips:</strong> Target {'<60'} days to beat 90-day average; use Prague Devs for quality</li>
                      <li><CheckCircle /><strong>US Tips:</strong> Aim for {'>80'}% acceptance with equity (0.2%); leverage IEEE for seniors</li>
                      <li><CheckCircle /><strong>Best Practices:</strong> Review monthly; shift channels if quality {'<90'}%; integrate with LinkedIn Talent Insights and Google Analytics</li>
                      <li><CheckCircle /><strong>Example Dashboard:</strong> Sample metrics for a Prague SaaS firm with complete data</li>
                    </>
                  ) : moduleId === 8 ? (
                    <>
                      <li><CheckCircle /><strong>Action Plan Structure:</strong> Action, Owner, Deadline (e.g., Week 1), Expected Outcome for each of 3 actions</li>
                      <li><CheckCircle /><strong>CZ Tips:</strong> Focus on hybrid work (70% preference) and Prague/Brno meetups; revise job descriptions, join Prague Devs, test GitHub outreach</li>
                      <li><CheckCircle /><strong>US Tips:</strong> Highlight equity (0.2–0.5%) and enterprise projects; update career pages, network at events, implement AI tools</li>
                      <li><CheckCircle /><strong>Best Practices:</strong> Assign clear owners; track metrics (time-to-hire {'<60'} days, {'>80'}% acceptance); review at day 30 to iterate</li>
                      <li><CheckCircle /><strong>Example Plan:</strong> Sample action plan for a Brno startup with complete actions, owners, deadlines, and outcomes</li>
                    </>
                  ) : (
                    <>
                      <li><CheckCircle />Job Title & Location sections</li>
                      <li><CheckCircle />Overview & Key Responsibilities</li>
                      <li><CheckCircle />Must-Have Skills (5 required)</li>
                      <li><CheckCircle />Nice-to-Have Skills (2-3 optional)</li>
                      <li><CheckCircle />Motivators (3 key motivators)</li>
                      <li><CheckCircle />Salary Range guidelines</li>
                      <li><CheckCircle />Benefits & Application Process</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
            
            {/* Upsell banners after takeaway */}
            {moduleConfig.upsells && moduleConfig.upsells.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                {moduleConfig.upsells.map((upsell, index) => (
                  <UpsellBanner
                    key={index}
                    moduleId={moduleId}
                    type={upsell.type}
                    title={upsell.title}
                    description={upsell.description}
                    ctaText={upsell.ctaText}
                    ctaLink={upsell.ctaLink}
                    features={upsell.features}
                    userId={user?.id}
                    onWaitingListClick={() => setShowWaitlistModal(true)}
                  />
                ))}
              </div>
            )}
          </div>
        );
        
      default:
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <IconComponent className="section-icon" />
              <h2>{section.title}</h2>
            </div>
            
            <div className="content-section" dangerouslySetInnerHTML={{ __html: String(section.content) }} />
          </div>
        );
    }
  };

  // Progress bar functionality removed - not currently used


  return (
    <div style={{ 
      minHeight: 'calc(100vh - 4rem)', 
      background: 'var(--white)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      paddingTop: '2rem'
    }}>
      <main className="module-content" style={{ 
        paddingBottom: '2rem',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="container">
          {/* Fixed Header Section */}
          <div className="module-fixed-header">
            <Link to={isPreviewMode ? (inviteToken ? `/?invite=${inviteToken}` : '/') : (isAdminMode ? '/admin/dashboard' : (user ? `/user/${user.id}/dashboard` : '/'))} className="back-link">
              <ChevronLeft />
              {isPreviewMode ? 'Back to Course Overview' : 'Back to Dashboard'}
            </Link>

            <div className="module-header">
              <div className="module-badge">
                Module {moduleId}
              </div>
              <h1>{moduleConfig.title}</h1>
              <p className="module-description">{moduleConfig.description}</p>
              <div className="module-meta">
                <div className="meta-item">
                  <Clock className="meta-icon" />
                  <span>{moduleConfig.duration}</span>
                </div>
                <div className="meta-item">
                  <Play className="meta-icon" />
                  <span>interactive + materials</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Timeline for Tablet/Mobile */}
          <div className="progress-timeline">
            <div className="timeline-container">
              {moduleConfig.sections.map((section, index) => {
                const Icon = section.icon;
                const isActive = currentSection === index;
                const isCompleted = index < currentSection;
                const isUpcoming = index > currentSection;
                
                return (
                  <div key={section.id} className="timeline-step">
                    <button
                      className={`timeline-button ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isUpcoming ? 'upcoming' : ''}`}
                      onClick={() => handleSectionChange(index)}
                    >
                      <div className="timeline-icon">
                        {isCompleted ? <CheckCircle size={18} color="#10b981" /> : <Icon size={18} />}
                      </div>
                      <div className="timeline-label">{section.shortTitle || section.title}</div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="module-layout">
            <div className="module-sidebar">
              <div className="progress-section">
                <h3>Module Progress</h3>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${((currentSection + 1) / moduleConfig.sections.length) * 100}%` }}
                  />
                </div>
                <span>{Math.round(((currentSection + 1) / moduleConfig.sections.length) * 100)}% Complete</span>
              </div>

              <nav className="section-nav">
                <h3>Sections</h3>
                {moduleConfig.sections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      className={`nav-item ${currentSection === index ? 'active' : ''} ${index < currentSection ? 'completed' : ''}`}
                      onClick={() => handleSectionChange(index)}
                    >
                      <Icon className="nav-icon" />
                      <span>{section.shortTitle || section.title}</span>
                      {index < currentSection && <CheckCircle className="completed-icon" />}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="module-content-wrapper">
              <div className="module-content">
                {renderSection()}
              </div>
              
              <div className={`section-navigation ${currentSection === 0 ? 'single-button' : ''}`}>
                {currentSection > 0 && (
                  <button 
                    className="nav-btn prev-btn"
                    onClick={() => handleSectionChange(currentSection - 1)}
                  >
                    <ChevronLeft />
                    Previous
                  </button>
                )}
                
                {currentSection < moduleConfig.sections.length - 1 && (
                  <button 
                    className="nav-btn next-btn"
                    onClick={() => handleSectionChange(currentSection + 1)}
                  >
                    Next
                    <ArrowRight />
                  </button>
                )}
                
                {currentSection === moduleConfig.sections.length - 1 && (
                  <button 
                    className="nav-btn complete-btn"
                    onClick={handleCompleteModule}
                    disabled={isCompleting || isModuleCompleted}
                    style={{
                      background: isModuleCompleted ? '#10b981' : undefined,
                      color: isModuleCompleted ? 'white' : undefined,
                      cursor: isModuleCompleted ? 'default' : 'pointer'
                    }}
                  >
                    {isCompleting ? (moduleId === 8 ? 'Finishing...' : 'Completing...') : isModuleCompleted ? `MODULE ${moduleId} COMPLETED` : (moduleId === 8 ? 'FINISH COURSE' : 'Complete Module')}
                    <CheckCircle />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Role Definition Success Modal */}
      {showRoleDefinitionSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: hasJDInSubmission ? '1rem' : '1.5rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Role definition submitted successfully! We'll review it as soon as possible and provide feedback via email.
            </p>
            {!hasJDInSubmission && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                textAlign: 'left'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <AlertTriangle 
                    size={20} 
                    style={{ 
                      color: '#6b7280', 
                      flexShrink: 0,
                      marginTop: '2px'
                    }} 
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{
                      margin: 0,
                      color: '#374151',
                      fontFamily: 'var(--montserrat)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      lineHeight: '1.5',
                      marginBottom: '0.5rem'
                    }}>
                      Important: No Job Description Provided
                    </p>
                    <p style={{
                      margin: 0,
                      color: '#4b5563',
                      fontFamily: 'var(--montserrat)',
                      fontSize: '0.85rem',
                      lineHeight: '1.5'
                    }}>
                      Without a Job Description (JD link or text), we don't have enough data to provide valid feedback. Your submission will be ignored to prevent us from focusing on incomplete tasks. If you want valid feedback, please resubmit with the full JD included—this ensures we can get back to you with personalized feedback.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                setShowRoleDefinitionSuccessModal(false);
                setHasJDInSubmission(false);
              }}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                width: '100%'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Module 4 - Employer Brand Audit Success Modal */}
      {showEmployerBrandAuditSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your employer brand audit has been submitted. We'll review it as soon as possible and offer tailored feedback (optional) to boost your brand.
            </p>
            <button
              onClick={() => setShowEmployerBrandAuditSuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Module 3 - Sourcing Practice Success Modal */}
      {showSourcingPracticeSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your sourcing practice has been submitted successfully. We'll review it as soon as possible and offer tailored feedback (optional) to help you source hidden talent effectively.
            </p>
            <button
              onClick={() => setShowSourcingPracticeSuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Module 5 - Screening & Engagement Success Modal */}
      {showScreeningEngagementSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your screening and engagement practice has been submitted. We'll review it as soon as possible and offer tailored feedback (optional) to refine your approach.
            </p>
            <button
              onClick={() => setShowScreeningEngagementSuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Module 6 - Pipeline Plan Success Modal */}
      {showPipelinePlanSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your pipeline plan has been submitted. We'll review it as soon as possible and offer tailored feedback (optional) to optimise your pipeline.
            </p>
            <button
              onClick={() => setShowPipelinePlanSuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Module 7 - Dataset Analysis Success Modal */}
      {showDatasetAnalysisSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your dataset analysis has been submitted. We'll review it as soon as possible and offer tailored feedback (optional) to optimise your hiring effectiveness.
            </p>
            <button
              onClick={() => setShowDatasetAnalysisSuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Module 8 - Action Plan Success Modal */}
      {showActionPlanSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your 30-day action plan has been submitted. We'll review it as soon as possible and offer tailored feedback (optional) to optimise your hiring strategy.
            </p>
            <button
              onClick={() => setShowActionPlanSuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Module 7 - Hands-On Activity Success Modal */}
      {showHandsOnActivitySuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ 
              marginBottom: '1rem', 
              color: 'var(--dark)', 
              fontFamily: 'var(--montserrat)',
              fontSize: '1.5rem'
            }}>
              Thank You!
            </h2>
            <p style={{ 
              marginBottom: '2rem', 
              color: 'var(--gray)', 
              fontFamily: 'var(--montserrat)',
              lineHeight: '1.6'
            }}>
              Your hands-on activity has been submitted successfully! We'll review it as soon as possible and offer tailored feedback (optional) to optimise your hiring strategy. Earn the "Transformation Closer" badge!
            </p>
            <button
              onClick={() => setShowHandsOnActivitySuccessModal(false)}
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'var(--montserrat)',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#d4a017';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--orange)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Module Completion Modal for Full Access Users */}
      {showCompletionModal && moduleId !== 8 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem auto'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            
            <h2 style={{
              color: 'var(--dark)',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '1rem',
              fontFamily: 'Montserrat, Arial, sans-serif'
            }}>
              🎉 Module {moduleId} - {moduleTitle} Completed!
            </h2>
            
            <p style={{
              color: 'var(--gray)',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              fontFamily: 'Montserrat, Arial, sans-serif'
            }}>
              🎉 Module {moduleId} - {moduleTitle} completed! You're ready to continue with the next module.
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              alignItems: 'stretch',
              width: '100%'
            }}>
              {moduleId === 3 && (
                <button
                  onClick={() => {
                    setShowWaitlistModal(true);
                  }}
                  style={{
                    background: 'var(--orange)',
                    color: 'var(--dark)',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#c4a020';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--orange)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Waiting list for Advanced
                </button>
              )}
              
              {moduleId === 2 ? (
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    if (user) {
                      navigate(`/user/${user.id}/module3`);
                    } else {
                      navigate('/');
                    }
                  }}
                  style={{
                    background: 'var(--dark)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#042789';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--dark)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Play size={16} />
                  Continue to Module 3
                </button>
              ) : moduleId === 3 ? (
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    if (user) {
                      navigate(`/user/${user.id}/module4`);
                    } else {
                      navigate('/');
                    }
                  }}
                  style={{
                    background: 'var(--dark)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#042789';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--dark)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Play size={16} />
                  Continue to Module 4
                </button>
              ) : moduleId === 4 ? (
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    if (user) {
                      navigate(`/user/${user.id}/module5`);
                    } else {
                      navigate('/');
                    }
                  }}
                  style={{
                    background: 'var(--dark)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#042789';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--dark)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Play size={16} />
                  Continue to Module 5
                </button>
              ) : moduleId === 5 ? (
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    if (user) {
                      navigate(`/user/${user.id}/module6`);
                    } else {
                      navigate('/');
                    }
                  }}
                  style={{
                    background: 'var(--dark)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#042789';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--dark)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Play size={16} />
                  Continue to Module 6
                </button>
              ) : moduleId === 6 ? (
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    if (user) {
                      navigate(`/user/${user.id}/module7`);
                    } else {
                      navigate('/');
                    }
                  }}
                  style={{
                    background: 'var(--dark)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#042789';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--dark)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Play size={16} />
                  Continue to Module 7
                </button>
              ) : moduleId === 7 ? (
                <button
                  onClick={() => {
                    setShowCompletionModal(false);
                    if (user) {
                      navigate(`/user/${user.id}/module8`);
                    } else {
                      navigate('/');
                    }
                  }}
                  style={{
                    background: 'var(--dark)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    width: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#042789';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--dark)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Play size={16} />
                  Continue to Module 8
                </button>
              ) : null}
              
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  // Navigate to admin dashboard if in admin mode, otherwise user dashboard
                  if (isAdminMode) {
                    window.location.href = '/admin/dashboard';
                  } else {
                    window.location.href = `/user/${user?.id}/dashboard`;
                  }
                }}
                style={{
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  width: '100%'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#c4a020';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--orange)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Home size={16} />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Course Completion Modal for Module 8 */}
      {showCourseCompletionModal && moduleId === 8 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10003
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '3rem',
            maxWidth: '600px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, var(--orange) 0%, #d4a017 100%)',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem auto'
            }}>
              <Trophy size={50} color="white" />
            </div>
            
            <h2 style={{
              color: 'var(--dark)',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              fontFamily: 'var(--montserrat)'
            }}>
              Congratulations! 🎉
            </h2>
            
            <p style={{
              color: 'var(--gray)',
              fontSize: '1.125rem',
              lineHeight: '1.8',
              marginBottom: '1.5rem',
              fontFamily: 'var(--raleway)'
            }}>
              You've completed the entire course! We hope you found it interesting, informative, and new insights that will help you in your recruitment journey.
            </p>
            
            <p style={{
              color: 'var(--dark)',
              fontSize: '1rem',
              lineHeight: '1.8',
              marginBottom: '2rem',
              fontFamily: 'var(--raleway)',
              fontWeight: '500'
            }}>
              However, this is just a teaser! We're preparing a more advanced deep-dive course for you that also includes insights on how to implement best practices in different fields such as hiring finance, banking, marketing, multilingual, and other positions.
            </p>
            
            <p style={{
              color: 'var(--gray)',
              fontSize: '1rem',
              lineHeight: '1.8',
              marginBottom: '2rem',
              fontFamily: 'var(--raleway)'
            }}>
              If you would be interested in taking the advanced course, kindly subscribe to our waiting list and we'll bring the course to you as soon as it's ready.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  setShowCourseCompletionModal(false);
                  setShowWaitlistModal(true);
                }}
                style={{
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'var(--montserrat)',
                  flex: '1',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#d4a017';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--orange)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Subscribe to Waitlist
              </button>
              
              <button
                onClick={() => {
                  setShowCourseCompletionModal(false);
                  // Trigger the team invitation form modal
                  const event = new CustomEvent('showInterestForm', {
                    detail: { formType: 'team-invitation' }
                  });
                  window.dispatchEvent(event);
                }}
                style={{
                  background: 'var(--dark)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'var(--montserrat)',
                  flex: '1',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#1a1a1a';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Invite Colleagues
              </button>
              
              <button
                onClick={() => {
                  setShowCourseCompletionModal(false);
                  if (user) {
                    navigate(`/user/${user.id}/dashboard`);
                  } else {
                    navigate('/');
                  }
                }}
                style={{
                  background: 'white',
                  color: 'var(--dark)',
                  border: '2px solid var(--dark)',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'var(--montserrat)',
                  flex: '1',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10004
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <WaitlistForm
              courseType="advanced"
              onClose={() => setShowWaitlistModal(false)}
              onSuccess={() => {
                setShowWaitlistModal(false);
                // Optionally show a success message or redirect
                if (user) {
                  navigate(`/user/${user.id}/dashboard`);
                } else {
                  navigate('/');
                }
              }}
            />
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}
